/**
 * generateLearningScript - Build complete learning script from Supabase
 *
 * Copied from dashboard (src/services/supabase.js) - this is the source of truth.
 * Same database, same query, same result.
 *
 * Round Structure:
 * 1. INTRO     - presentation audio ("The Dutch for X is...")
 * 2. DEBUT     - the LEGO itself (known → target)
 * 3. BUILD ×7  - up to 7 BUILD phrases (drilling)
 * 4. SPACED REP - USE phrases from older LEGOs (max 12, Fibonacci timing)
 * 5. USE ×2    - exactly 2 USE phrases (consolidation)
 */

import { sampleWithoutReplacement } from './sampleWithoutReplacement'
import type { SupabaseClient } from '@supabase/supabase-js'
import type { CourseBundle } from '@ssi/core'
import {
  countTargetSyllables,
  reviewOffsets,
  SEED_REVIEW_START_OFFSET,
  SEED_SENTENCE_KIND,
  assignSeedSentenceHosts,
  seedHasAllClips,
  seedSentenceCycleId,
  seedSentenceReviewEnabled,
  seedSentenceReviewsDue,
  seedsByHostRound,
} from '@ssi/core'
import type { Round as PlayerRound } from '../playback/SimplePlayer'
import { validateLearningScript } from './validateLearningScript'
import { applyAudioRef, collectAudioIds, getRevisedAudioRefs, stampRowAudioRefs } from './revisedAudioRefs'
// The phrase-length cap lives with the mode config it comes from — ONE place,
// next to resolveScriptShape (Aran's correction, 2026-08-06).
import {
  capPhrasesByLength, courseMaxPhraseLength, phraseTextLength,
  normalizeMaxPhraseLengthFraction, normalizeMaxKnownSyllables,
  normalizeReviewFilterMaxRound, makeKnownSyllableResolver, filterReviewPool,
  normalizePhraseRepeatCount, normalizeRepeatedCycleTypes,
  normalizeUseWordCapTiers, makeUseWordCap, capUsePoolByWords,
  MIN_BUILD_PHRASES_AFTER_CAP, MIN_USE_PHRASES_AFTER_CAP,
} from '../composables/useAlgorithmConfig'
import type { ReviewPullFilter, UseWordCap, UseWordCapTier } from '../composables/useAlgorithmConfig'
import { capConsecutiveRepeats } from '../playback/capConsecutiveRepeats'
import { resolveServedPod } from '../composables/servedPod'
import { repeatPhraseCycles } from './repeatPhraseCycles'
import { authoredGlossSegments } from '../utils/authoredGlossSegments'
import { ensureRunningOrder, orderSeedNumbers } from '../lib/runningOrder'

export interface ScriptItem {
  uuid: string
  cycleNum: number
  roundNumber: number
  seedId: string
  legoKey: string
  seedCode: string
  legoCode: string
  type: 'intro' | 'debut' | 'build' | 'spaced_rep' | 'use' | 'listening' | 'component_intro' | 'component_practice' | 'listen_intro' | 'listen_outro' | 'pod'
  knownText: string
  targetText: string
  /** Native script text — only set when targetText is romanized */
  targetTextNative?: string
  presentationAudioId?: string
  knownAudioId?: string
  target1Id?: string
  target2Id?: string
  target1DurationMs?: number
  target2DurationMs?: number
  isNew: boolean
  syllableCount?: number
  fibPosition?: number
  reviewOf?: number
  /** Set to 'seed' when this spaced_rep item is a WHOLE-SEED visit (offset 89
   * or 144): the payload is the full parent seed sentence rather than a
   * use-phrase. It is an ORDINARY four-phase cycle — known audio, gap, target
   * voice 1, target voice 2 — so no reader special-cases it; the marker is for
   * telemetry and traces. (It is NOT the retired t→k→t→t sandwich, which had
   * one clip per item and is what reached mintonman as a lone Basque flash.)
   *
   * 'seed_sentence' (job #495, courses switched on in @ssi/core's
   * SEED_SENTENCE_REVIEW_COURSES only): the complete sentence of a seed that
   * introduces NO new LEGO, on its own schedule — first at the end of its host
   * round, then 1, 2, 3, 5 … 144 rounds later. Same four-phase cycle, same
   * three seed clips. Its legoKey is EMPTY on purpose: the sentence is not
   * filed under any LEGO (Kai's P26/L30). */
  reviewItemKind?: 'seed' | typeof SEED_SENTENCE_KIND
  /** Which loop this item's round belongs to — see core Round.revival. */
  revival?: boolean
  componentLegoIds?: string[]
  componentLegoTexts?: string[]
  /** Native script variants — only set when romanized text exists */
  componentLegoTextsNative?: string[]
  /** Authoritative content-level tiling from course_practice_phrases.decomposition,
   * carried verbatim for phrase-sourced items. Player renders it directly. */
  decomposition?: Array<{ legoId: string | null; target: string; known: string; isGhost: boolean; isSalient?: boolean }>
  /** Authored display tiles from course_practice_phrases.display_tiling —
   * {n: native, r: roman, salient} per tile, built and validated in Popty.
   * When present the player renders these directly (native glyph primary,
   * roman as ruby) instead of running the device segmenter. */
  displayTiling?: Array<{ n: string; r: string; salient?: boolean }>

  /** M-LEGO component breakdown: [{known: "with", target: "con"}, ...] */
  components?: Array<{ known: string; target: string }>
  /** Native script variant of components */
  componentsNative?: Array<{ known: string; target: string }>
  /** The intro's AUTHORED word mapping — literal known chunks in the TARGET's
   *  own word order, each covering `span` consecutive target words. Preferred
   *  over `components` by the tile assembler; componentisation stays the
   *  fallback (Tom, 2026-08-13). */
  glossSegments?: Array<{ span: number; known: string }>
  /** Listening phase: playback speed multiplier (1.0 = normal, 2.0 = double) */
  playbackSpeed?: number
  /** Listening phase: which seed this listening item is for */
  listeningSeedNumber?: number
}

/**
 * Default per-round script shape — mirrored to algorithm_config.script_shape.
 * Changing these reshapes every round generated after the change.
 */
export interface ScriptShape {
  spacedRepOffsets: number[]
  maxBuildPhrases: number
  useConsolidationCount: number
  maxSpacedRepPhrases: number
  n1PhraseCount: number
}

export const DEFAULT_SCRIPT_SHAPE: ScriptShape = {
  // Eleven Fibonacci offsets, stopping at 144. 1..55 are LEGO-level
  // use-phrase reviews; 89 and 144 are the WHOLE-SEED rungs (Tom, 2026-09-20 —
  // see reviewItemIsSeed below). The live algorithm_config.script_shape row is
  // the runtime source of truth and carries the full tail, which
  // `reviewOffsets()` trims at 233; this is only the fallback when that fetch
  // fails.
  spacedRepOffsets: [1, 2, 3, 5, 8, 13, 21, 34, 55, 89, 144],
  maxBuildPhrases: 7,
  useConsolidationCount: 2,
  maxSpacedRepPhrases: 12,
  n1PhraseCount: 3,
}

/**
 * WHOLE-SEED RUNGS, identical on this path and on the bundle path (Tom,
 * 2026-09-20: "maybe it's better to have that 89 or maybe from 89 and also 89
 * and 144"). At offsets 89 and 144 the review is the LEGO's full parent SEED
 * sentence rather than a use-phrase, served as an ORDINARY four-phase
 * production cycle, ONCE PER SEED per rung — only the seed's LAST LEGO (its
 * anchor, see `seedAnchorLegos`) emits it, so a four-LEGO seed does not come
 * back four rounds running.
 *
 * This is emphatically NOT the retired t→k→t→t comprehensible-input sandwich
 * that used to sit at ≥144: four single-clip sub-items, no gap, no voice 2,
 * which the duplicate pass thinned to one target clip and reached mintonman as
 * a flash. Everything inside a main round is a complete prompt → gap → voice 1
 * → voice 2 cycle, or it is not emitted at all.
 *
 * `reviewOffsets` (@ssi/core) is the shared filter — it trims the configured
 * `algorithm_config.script_shape` row's long Fibonacci tail at 233, so the
 * ceiling holds without a DB edit.
 */
export { SEED_REVIEW_START_OFFSET }

/** Is a spaced-rep review at this skip offset a whole-seed visit? */
export function reviewItemIsSeed(offset: number): boolean {
  return offset >= SEED_REVIEW_START_OFFSET
}

// Role → runtime playback rate for Layer-2 pod plays (emitPodLap, retained
// for hot-fix rollback — see the comment above emitPodLap). All audio
// (target or known) plays back at the role's speed; 'trans' is always 1.0×
// because the known-language clip is reference material.
const ROLE_SPEED: Record<string, number> = {
  ps08x: 0.8,
  ps: 1.0,
  ps15x: 1.5,
  ps2x: 2.0,
  trans: 1.0,
}

// Per Aran's listening-layers spec.
//
// 2026-05-19: L1 listening pulled out of the main flow, replaced by the
// runtime 30-cup wheel (useLayer1Scheduler.ts). L2 stays in (every
// POD_ROUND_INTERVAL rounds, runtime-scheduled). 2026-09-18: seed
// graduation went with the SEED-PHASE review tier — the generator no
// longer tracks it at all, so `offset` is inert here.
export interface ListeningConfig {
  enabled: boolean
  /** @deprecated INERT since 2026-09-18 — it gated the deleted SEED-PHASE
   *  review tier. Kept so stored `algorithm_config.listening` rows parse. */
  offset: number
  // Layer 2 — Pod 0
  /** First pod lap fires at end of this main round (start of seed 2).
   *  Optional now that the field's primary home is PodsConfig — callers
   *  that pass the listening config without merging it in still work;
   *  the generator falls back to 6 (matches DEFAULT_POD_ACTIVATION). */
  podActivationRound?: number
}

export const DEFAULT_LISTENING_CONFIG: ListeningConfig = {
  enabled: true,
  // Graduation offset: rounds after a seed's LAST LEGO debut before it
  // becomes eligible for L1 listening. Calibrated to N-89 spaced-rep
  // decay — final fib review for the last LEGO is at lastRound + 89,
  // graduation one round later means the seed enters listening only
  // after every one of its LEGOs has fully dropped out of spaced rep.
  offset: 90,
  podActivationRound: 6,
}

export interface LearningScriptResult {
  items: ScriptItem[]
  /**
   * Pre-built player rounds — present ONLY on the bundle path (cutover step 6,
   * `providers/bundleFullScript.ts`), where the whole course is materialised
   * from the in-memory bundle and `items` is empty because there is no
   * ScriptItem stage to pass through. Consumers must read this FIRST and fall
   * back to converting `items` (see LearningPlayer's `scriptRounds` helper);
   * the walk itself never sets it.
   */
  rounds?: PlayerRound[]
  /**
   * The bundle the rounds were built from, on the bundle path only. Carried so
   * derived maps that used to be computed from the walk's items (centrality)
   * can be computed from their real source instead.
   */
  bundle?: CourseBundle
  cycleCount: number
  roundCount: number
  /**
   * Number of MAIN-LOOP rounds in the playable output (i.e. where the INF-PLAY
   * revival tail begins, as a 0-based index into the rounds). Derived from the
   * generator's OWN boundary (mainLoopLastRound) over the PLAYABLE items — so it
   * is the true current course size (sparse LEGO ordinal, unbuilt/no-audio seeds
   * already excluded). The single source of truth for "where INF PLAY starts";
   * callers must NOT re-derive it from a DB count, which diverges.
   */
  mainLoopRoundCount: number
  hasRomanizedText: boolean
  /**
   * Seed-sentence review counts (job #495) — set only on a course switched on
   * in @ssi/core's SEED_SENTENCE_REVIEW_COURSES. `hosted` nothing-new seeds
   * were given a host round; `skippedMissingAudio` of them (or of unhosted
   * ones) lack one of the three seed clips and never play; `unhosted` came
   * before any round.
   */
  seedSentences?: { hosted: number; skippedMissingAudio: number; unhosted: number }
  /**
   * Did the KNOWN-side review/consolidate pull filter actually apply?
   *
   * False when the mode sets no filter (Fast), OR when this course's KNOWN
   * language has no registered syllable counter. The flag exists so the
   * filter's inertness is VISIBLE to callers rather than being an invisible
   * no-op, which is exactly how the first syllable attempt failed. Pairs with
   * the once-per-course console warning from makeKnownSyllableResolver.
   */
  syllableCapApplied: boolean
  /**
   * Did the sliding USE word cap actually apply (Tom, 2026-08-09)?
   *
   * False when the mode carries no ladder (Fast), or when English is on
   * NEITHER side of this course — the deferred case, where Easy behaves
   * exactly as it did before. Same visibility discipline as syllableCapApplied:
   * an inert cap must be readable, not invisible.
   */
  useWordCapApplied: boolean
}

/**
 * The Easy-mode levers, all off by default — so a caller that passes nothing
 * (and Fast, which passes them all off explicitly) generates byte-identical
 * output to the pre-2026-08-07 walk.
 */
export interface EasyModeOptions {
  /**
   * How many times each eligible practice cycle plays, back to back (Tom,
   * 2026-08-07). 1 / absent ⇒ once, which is Fast. Easy ships 2, and 2 is also
   * the hard ceiling — a higher value is clamped, loudly.
   */
  phraseRepeatCount?: number
  /** Which cycle types the repeat applies to. Defaults to build / spaced_rep /
   *  use — BLD, REVIEW, USE and CONSOLIDATE. */
  repeatedCycleTypes?: string[]
  /** False ⇒ BUILD phrases bypass the phrase-length cap entirely ("no
   *  filtering on BLD phrases" — Tom, 2026-08-07). Default true. */
  filterBuildPhrases?: boolean
  /** Max KNOWN-language syllables for a review/consolidate pull. 0 ⇒ off. */
  reviewMaxKnownSyllables?: number
  /** Last round on which that filter applies; it lifts from the next round. */
  reviewSyllableFilterMaxRound?: number
  /**
   * Sliding word-count cap on USE phrases, by round (Tom, 2026-08-09). Empty /
   * absent ⇒ off, which is Fast. Easy ships 8 words to round 20, 10 to round
   * 100, uncapped after. Counted on the ENGLISH side of the pair and applied
   * only on courses with English on one side — see ModeConfig.useWordCapTiers.
   */
  useWordCapTiers?: UseWordCapTier[]
}

/**
 * A CYCLE ID THAT NAMES THE PHRASE IT PLAYS.
 *
 * `S0042L03_use_05_review_7` — the LEGO, then the phrase's own role and index
 * as `course_practice_phrases.id` carries them (`…:S0042L03U05`), then what
 * the cycle is doing with it and the script counter that keeps it unique.
 * Same shape the bundle path already stamps, so ONE parser reads both.
 *
 * The counter alone is not a name. Until 2026-09-17 the walk stamped
 * `S0042L03_build_11827`, and the class brain — reading two digits after
 * `_build_` — called that BUILD 11 and drew the card from a sentence the class
 * had never heard. A phrase with no readable row id keeps the old counter-only
 * form, which the brain now resolves to NOTHING rather than to a wrong guess.
 *
 * `phraseRowId` is the row's id; everything after the colon is the name.
 */
export function phraseCycleId(
  legoKey: string,
  phraseRowId: string | undefined,
  kind: 'build' | 'use' | 'review' | `inf_R${number}` | `inf_sr_R${number}`,
  cycleNum: number,
): string {
  const m = /S\d{4}L\d{2}(B|U)(\d{2})$/.exec(phraseRowId || '')
  if (!m) return `${legoKey}_${kind}_${cycleNum}`
  return `${legoKey}_${m[1] === 'U' ? 'use' : 'build'}_${m[2]}_${kind}_${cycleNum}`
}

// Deliberately EXCLUDES the two heavy JSON columns `decomposition` and
// `display_tiling`. Across a big course's 15-17k phrase rows those two
// dominate the payload, yet the full-course walk only needs them for the
// handful of rounds actually RENDERED — and rendering is served per-round by
// the instant-playback /cycles path (useInstantPlayback → backendCyclesToRounds),
// which fetches both columns for exactly the rounds on screen. Pulling them
// across the whole course here was pure up-front tax. Script items generated
// from this fetch simply omit decomposition/displayTiling (nativeFields guards
// on presence); any round that reaches the screen gets its authored tiling from
// /cycles. See the course-load-window fix.
const PRACTICE_PHRASE_COLUMNS =
  'id, seed_number, lego_index, known_text, target_text, target_text_roman, phrase_role, target_syllable_count, position, known_audio_id, target1_audio_id, target2_audio_id, presentation_audio_id, target1_duration_ms, target2_duration_ms, introduce'

/**
 * Fetch ALL course_practice_phrases for a course, paginated.
 *
 * A single `.limit(N)` is silently capped by PostgREST's server-side max-rows
 * (>=10000 here). Big courses carry 15-17k phrase rows, so the old single
 * `.limit(10000)` query dropped every phrase past ~seed 405 — the back 40-56%
 * of the course had NO build/use phrases, which starved INF PLAY's random-USE
 * pool AND the SR-drain (revival rounds came out as ~4-16 cycles of mostly the
 * front of the course, never the canonical 22, and the spaced review of the
 * final LEGOs played nothing). So the read is paginated — and NOT by a deep
 * .range() over the whole course: an OFFSET re-walks every row before it (6.6 s
 * at offset 15000 on spa_for_eng against 2.7 ms at 0), and the old count(exact)
 * walked the whole course too (job #806, adversary #828·P). The course is cut
 * into windows of SEED_WINDOW seeds, fetched six at a time, each paged from
 * offset 0, so offsets stay shallow and a dense window still comes back whole.
 * Window order is seed order: concatenation keeps (seed, lego, position).
 * Mirrors fetchAllBundlePhrases in api/courses/[code]/bundle.ts.
 */
const PRACTICE_PHRASE_PAGE = 1000
const PRACTICE_SEED_WINDOW = 25
const WINDOW_CONCURRENCY = 6

// At most WINDOW_CONCURRENCY windows in flight (adversary round 2): window
// count follows the seed SPAN, and the Welsh v2 courses span seeds 1-1668, so
// unbounded that is ~67 parallel requests from one learner onto a 2-core
// database's PostgREST pool. Results keep window order.
async function mapWindows<T>(windows: Array<[number, number]>, limit: number, fn: (w: [number, number]) => Promise<T>): Promise<T[]> {
  const out: T[] = new Array(windows.length)
  let next = 0
  const lane = async () => { while (next < windows.length) { const i = next++; out[i] = await fn(windows[i]) } }
  await Promise.all(Array.from({ length: Math.min(limit, windows.length) }, lane))
  return out
}

async function fetchPracticePhraseWindow(
  supabase: SupabaseClient,
  courseCode: string,
  from: number,
  to: number,
): Promise<{ data: any[] | null; error: any }> {
  const rows: any[] = []
  // Bounded: a window is a few hundred rows, so 50 pages means something is wrong.
  for (let offset = 0; offset < 50 * PRACTICE_PHRASE_PAGE; offset += PRACTICE_PHRASE_PAGE) {
    const { data, error } = await supabase
      .from('course_practice_phrases')
      .select(PRACTICE_PHRASE_COLUMNS)
      .eq('course_code', courseCode)
      .gte('seed_number', from)
      .lte('seed_number', to)
      .order('seed_number', { ascending: true })
      .order('lego_index', { ascending: true })
      .order('position', { ascending: true })
      .range(offset, offset + PRACTICE_PHRASE_PAGE - 1)
    if (error) return { data: null, error }
    if (data) rows.push(...data)
    if (!data || data.length < PRACTICE_PHRASE_PAGE) return { data: rows, error: null }
  }
  return { data: null, error: { message: `phrase window ${from}-${to} did not end within 50 pages` } }
}

async function fetchAllPracticePhrases(
  supabase: SupabaseClient,
  courseCode: string,
): Promise<{ data: any[] | null; error: any }> {
  const edge = (ascending: boolean) => supabase
    .from('course_practice_phrases')
    .select('seed_number')
    .eq('course_code', courseCode)
    .order('seed_number', { ascending })
    .limit(1)
  const [lo, hi] = await Promise.all([edge(true), edge(false)])
  if (lo.error) return { data: null, error: lo.error }
  if (hi.error) return { data: null, error: hi.error }
  const seeds = [...(lo.data ?? []), ...(hi.data ?? [])]
    .map((r: any) => Number(r.seed_number))
    .filter(Number.isFinite)
  if (seeds.length === 0) return { data: [], error: null }
  const first = Math.min(...seeds)
  const last = Math.max(...seeds)
  const windows: Array<[number, number]> = []
  for (let s = first; s <= last; s += PRACTICE_SEED_WINDOW) windows.push([s, Math.min(s + PRACTICE_SEED_WINDOW - 1, last)])
  const pages = await mapWindows(windows, WINDOW_CONCURRENCY, ([from, to]) => fetchPracticePhraseWindow(supabase, courseCode, from, to))
  const all: any[] = []
  for (const p of pages) {
    if (p.error) return { data: null, error: p.error }
    if (p.data) all.push(...p.data)
  }
  return { data: all, error: null }
}

// ————————————————————————————————————————————————————————————————————————
// Cooperative yielding for the whole-course walk. The walk is idle-scheduled
// the moment READY paints (LearningPlayer's ready-gated handoff), and its
// build loops used to run as ONE synchronous task — measured as a 10.9s
// main-thread block on a 4x-throttled CPU right after READY, which is the
// post-READY input dead zone (founder 2026-07-30: READY must mean
// INTERACTIVE, not painted). Yielding to the event loop between ~40ms slices
// lets input events and rendering interleave with the walk; the walk still
// completes long before its output is consumed (INF-PLAY boundary / warm
// cache / resume repair).
const WALK_SLICE_BUDGET_MS = 40

/**
 * Prompt identity for the A-64 consecutive-repeat cap: what the learner
 * actually hears as "the same thing again".
 *
 * Default per Tom's brief — normalised known text paired with normalised target
 * text, the same notion `getPhraseId` uses for within-round de-duplication.
 * Listening items are the one case that notion cannot see: a pod sentence play
 * carries no known text and a pod translation carries no target text, so two
 * plays of the same clip at different speeds ('ps' vs 'ps2x') would otherwise
 * look like different prompts. For those we fall back to the audio ids, which
 * is the honest answer — it is the same clip.
 */
export function scriptItemIdentity(item: ScriptItem): string {
  const norm = (text: string | null | undefined): string =>
    text ? text.toLowerCase().trim().replace(/[.,!?;:¡¿'"]+/g, '') : ''
  const known = norm(item.knownText)
  const target = norm(item.targetText)
  if (known || target) return `${known}|${target}`
  return `audio:${item.knownAudioId ?? ''}|${item.target1Id ?? ''}`
}

export const yieldToEventLoop = (): Promise<void> => {
  const sched = (globalThis as any).scheduler
  if (sched && typeof sched.yield === 'function') {
    // scheduler.yield(): continuation-priority resume, input runs first.
    return sched.yield().catch(() => new Promise<void>((r) => setTimeout(r, 0)))
  }
  if (typeof MessageChannel !== 'undefined') {
    // MessageChannel beats setTimeout's ~4ms nested-timer clamp; a fresh
    // channel per yield so concurrent walks can never steal each other's wake.
    return new Promise<void>((resolve) => {
      const ch = new MessageChannel()
      ch.port1.onmessage = () => { ch.port1.close(); resolve() }
      ch.port2.postMessage(null)
    })
  }
  return new Promise<void>((r) => setTimeout(r, 0))
}

/**
 * Returns an awaitable tick: a no-op while the current slice is under
 * budget, a real event-loop yield once it exceeds it. Sprinkled through the
 * walk's hot loops so no single main-thread task outlives ~budgetMs.
 */
export const makeSliceYielder = (budgetMs: number = WALK_SLICE_BUDGET_MS): (() => Promise<void>) => {
  const now = () => (typeof performance !== 'undefined' ? performance.now() : Date.now())
  let sliceStart = now()
  return async () => {
    if (now() - sliceStart < budgetMs) return
    await yieldToEventLoop()
    sliceStart = now()
  }
}

/** Ids per course_audio lookup — see the note inside stampSeedVisitDurations. */
const SEED_AUDIO_LOOKUP_CHUNK = 150

/**
 * Stamp the whole-seed visits with their two target clip durations, read from
 * `course_audio`.
 *
 * `course_seeds` has no duration columns — only `course_legos` and
 * `course_practice_phrases` do — so without this every seed visit would take
 * `computePauseDuration(0, 0)`, which clamps to the 1000 ms floor. The parent
 * SENTENCE is the longest thing a learner produces in a round, so giving it
 * the shortest gap in the round is exactly backwards. `api/courses/[code]/
 * bundle.ts`'s `fetchSeedAudioDurations` does the same job for the bundle path
 * and carries the full rationale, including why the lookup is CHUNKED: a bare
 * `.in()` over a thousand ids is a 38 KB GET the Supabase gateway rejects
 * outright (job #804, live 2026-09-15).
 *
 * Non-fatal by design: on any failure the visits keep the fallback gap rather
 * than costing the learner the whole script.
 */
async function stampSeedVisitDurations(
  supabase: SupabaseClient,
  items: ScriptItem[],
): Promise<void> {
  const visits = items.filter((i) => i.reviewItemKind === 'seed' || i.reviewItemKind === SEED_SENTENCE_KIND)
  if (visits.length === 0) return
  const bare = new Map<string, string>()
  for (const v of visits) {
    for (const id of [v.target1Id, v.target2Id]) {
      if (id) bare.set(id.split('.')[0], id)
    }
  }
  if (bare.size === 0) return
  const durations = new Map<string, number>()
  try {
    const bareIds = [...bare.keys()]
    const pages: string[][] = []
    for (let i = 0; i < bareIds.length; i += SEED_AUDIO_LOOKUP_CHUNK) {
      pages.push(bareIds.slice(i, i + SEED_AUDIO_LOOKUP_CHUNK))
    }
    const results = await Promise.all(
      pages.map((page) => supabase.from('course_audio').select('id, duration_ms').in('id', page)),
    )
    for (const { data, error } of results) {
      if (error) {
        console.warn('[generateLearningScript] seed audio duration lookup failed (non-fatal):', error.message)
        continue
      }
      for (const row of (data || []) as Array<{ id: string; duration_ms: number | null }>) {
        if (typeof row.duration_ms !== 'number' || row.duration_ms <= 0) continue
        const stamped = bare.get(row.id)
        if (stamped) durations.set(stamped, row.duration_ms)
        durations.set(row.id, row.duration_ms)
      }
    }
  } catch (err) {
    console.warn('[generateLearningScript] seed audio duration lookup threw (non-fatal):', err)
    return
  }
  for (const v of visits) {
    const d1 = v.target1Id ? durations.get(v.target1Id) : undefined
    const d2 = v.target2Id ? durations.get(v.target2Id) : undefined
    if (d1) v.target1DurationMs = d1
    if (d2) v.target2DurationMs = d2
  }
}


export async function generateLearningScript(
  supabase: SupabaseClient,
  courseCode: string,
  /**
   * Number of revival (infinite-play) rounds to emit AFTER the main loop
   * exhausts every is_new LEGO. The main-loop walk is always full-course;
   * this only controls how far the post-main tail extends. Default 50.
   */
  infinitePlayLookahead: number = 50,
  listeningConfig: ListeningConfig = DEFAULT_LISTENING_CONFIG,
  scriptShape: ScriptShape = DEFAULT_SCRIPT_SHAPE,
  /**
   * Cap on phrase length for the active learning mode, as a fraction of the
   * longest phrase available for each LEGO. 1.0 (the default) is uncapped and
   * reproduces the pre-2026-08-06 behaviour exactly; Easy ships 0.5.
   * See capPhrasesByLength() — the one place the rule lives.
   */
  maxPhraseLengthFraction: number = 1,
  /**
   * The active mode's Easy levers — doubling, the BUILD-filter switch, and the
   * known-side review/consolidate pull filter (Tom, 2026-08-07). Everything is
   * off by default, and Fast passes them all off, so Fast's script is provably
   * unchanged. Replaces the absolute TARGET-syllable ceiling that briefly
   * occupied this slot earlier the same night.
   */
  easyOptions: EasyModeOptions = {},
  /**
   * Fire a pod-lap every N main rounds from podActivationRound onward.
   * Mirrors PodsConfig.roundInterval — passed in so the generator's
   * L1-outro merge decision stays in sync with the runtime scheduler.
   * Default 1 (every round, legacy behaviour).
   */
  podRoundInterval: number = 5,
  /**
   * Seeded [0,1) random source for the INF-PLAY revival / random-USE tail.
   * When supplied, the long-tail steady-state USE stream becomes deterministic
   * for a given learner+position (so online INF PLAY is navigable and matches
   * the offline seeded model — coordinator decision 2026-05-29). Omit for the
   * legacy unseeded behaviour; the MAIN LOOP never consumes this, so main play
   * is unaffected either way.
   */
  infplayRandom?: () => number,
): Promise<LearningScriptResult> {
  // Per-round shape — DB-tweakable via algorithm_config.script_shape.
  // Offsets run through the SHARED ceiling (@ssi/core `reviewOffsets`): the
  // live config row still carries the long Fibonacci tail, and everything at
  // or past 144 belonged to the deleted SEED-PHASE tier. 89 is the last
  // review on every path.
  const SPACED_REP_OFFSETS = reviewOffsets(scriptShape.spacedRepOffsets)
  const MAX_BUILD_PHRASES = scriptShape.maxBuildPhrases
  const USE_CONSOLIDATION_COUNT = scriptShape.useConsolidationCount
  const MAX_SPACED_REP_PHRASES = scriptShape.maxSpacedRepPhrases
  const N1_PHRASE_COUNT = scriptShape.n1PhraseCount

  // Phrase-length cap (mode row). The ceiling is ABSOLUTE and COURSE-WIDE —
  // a fraction of the longest phrase in the whole course, measured in
  // characters of target text — computed once below, after the phrase pools
  // are known. 1.0 = uncapped = historic behaviour; Easy ships 0.5. Ordering
  // is always shortest-first; the cap only removes the long tail.
  const PHRASE_LENGTH_FRACTION = normalizeMaxPhraseLengthFraction(maxPhraseLengthFraction)
  const phraseLengthOf = (p: Phrase): number => phraseTextLength(p.target_text)

  // Resolved BEFORE the query batch below, because both levers decide whether
  // the courses-table read is issued at all.
  const MAX_KNOWN_SYLLABLES = normalizeMaxKnownSyllables(easyOptions.reviewMaxKnownSyllables)
  const USE_WORD_CAP_TIERS = normalizeUseWordCapTiers(easyOptions.useWordCapTiers)

  const normalizeText = (text: string | null | undefined): string => {
    if (!text) return ''
    return text.toLowerCase().trim().replace(/[.,!?;:¡¿'"\u3000-\u303f\uff00-\uff0f\uff1a-\uff20\uff3b-\uff40\uff5b-\uff65]+/g, '')
  }

  const getPhraseId = (knownText: string, targetText: string): string => {
    return `${normalizeText(knownText)}|${normalizeText(targetText)}`
  }

  /** A phrase's target syllable count: the stored value, else derived.
   *  `countTargetSyllables` is imported from `@ssi/core` — the walk and the
   *  bundle path must rank a basket by the same key or their debuts diverge. */
  const phraseSyllables = (phrase: {
    target_syllable_count?: number
    target_text?: string | null
  }): number => phrase.target_syllable_count || countTargetSyllables(phrase.target_text)

  // Query tables directly - audio IDs stored on each row, no joins needed.
  // ALL course-content queries are course-wide — no startSeed/endSeed
  // filtering. The script generator always walks the full course. Chunking
  // by seed range was the original cause of the L1-listening silent-fail
  // bug: any course-wide derivation (graduated seeds, anchor ordinals,
  // cross-LEGO references) needs the whole inventory in scope, not just
  // the current chunk's window.
  // The course_seeds fetch came BACK on 2026-09-20 with the whole-seed visit
  // (its only consumer — the cups listening interlude does its own reads, via
  // listeningMetaCache). The course-wide LEGO catalogue query did NOT: it
  // existed only to give every LEGO an absolute ordinal for seed graduation,
  // which the anchor rule replaces, so the last bare `.limit(10000)` on this
  // path stays gone.
  const [legosResult, phrasesResult, seedsResult, bookendsResult, podsResult, courseRowResult] = await Promise.all([
    supabase
      .from('course_legos')
      .select('seed_number, lego_index, known_text, target_text, target_text_roman, type, is_new, known_gloss_segments, known_audio_id, target1_audio_id, target2_audio_id, presentation_audio_id, target1_duration_ms, target2_duration_ms')
      .eq('course_code', courseCode)
      .order('seed_number', { ascending: true })
      .order('lego_index', { ascending: true })
      .limit(5000),
    // Paginated — a single .limit() is capped by PostgREST max-rows and big
    // courses have 15-17k phrase rows; truncation starved INF PLAY (see helper).
    fetchAllPracticePhrases(supabase, courseCode),
    // Seed sentences — the payload of the whole-seed visit at offsets 89/144.
    // `course_seeds` carries NO duration columns (unlike course_legos and
    // course_practice_phrases), so the visit's gap is stamped afterwards from
    // course_audio — see stampSeedVisitDurations below.
    supabase
      .from('course_seeds')
      .select('seed_number, known_text, target_text, target_text_roman, known_audio_id, target1_audio_id, target2_audio_id')
      .eq('course_code', courseCode)
      .order('seed_number', { ascending: true }),
    // Pre-fetch the two LISTEN-block bookend audio rows for this course.
    // Generated by scripts/generate-listen-bookends.cjs in the dashboard repo;
    // missing rows just mean this course's bookends haven't been generated yet
    // and Phase 6 will skip emitting them silently.
    listeningConfig.enabled
      ? supabase
          .from('course_audio')
          .select('role, text, id, duration_ms')
          .eq('course_code', courseCode)
          .in('role', ['bookend_listen_intro', 'bookend_listen_outro'])
      : Promise.resolve({ data: [], error: null }),
    // Pre-fetch this course's pod sentences (Layer 2 listening — round-end lap
    // after activation). Pod ID convention: "${course_code}:${slug}", and
    // WHICH slug is resolved per course by servedPod — pods went 1-based on
    // 2026-08-22, so every course serves `pod-1`.
    // Resolution is chained rather than awaited above so the other seven
    // queries here still fire immediately; it is memoised per course, so the
    // pod readers share one round-trip. Sentences ordered by global_order;
    // entry into the lap is 1 sentence/round. Returns empty if the course has
    // no served pod — Phase 7 silently skips.
    listeningConfig.enabled
      ? resolveServedPod(supabase, courseCode).then(({ podId }) =>
          supabase
            .from('listening_pod_sentences')
            .select('global_order, target_text, known_text, target_audio_id, known_audio_id')
            // BASE ROWS ONLY. This lane builds a LINEAR script and has no way to
            // offer a branch, so a continuation here would simply become an extra
            // line of everybody's walk. Filtered in the query rather than after
            // it: the rows never need to travel, and there is no client-side
            // classification to get wrong. (The branch-capable readers —
            // useListeningPods, usePodLapScheduler — fetch both and partition
            // with composables/podSlate.ts.)
            .is('variant_key', null)
            .eq('pod_id', podId)
            .order('global_order', { ascending: true }),
        )
      : Promise.resolve({ data: [], error: null }),
    // The course's LANGUAGES — what the review/consolidate pull filter needs
    // (known side) and what the USE word cap needs (which side is English).
    // Smallest possible fetch: two columns, one row, in the existing parallel
    // batch so it costs no latency. Skipped entirely when neither lever is set
    // (Fast), so Fast issues exactly the queries it always did. A failed or
    // missing row leaves both null, which makes both levers inert and warn —
    // never throws, never guesses.
    MAX_KNOWN_SYLLABLES < Infinity || USE_WORD_CAP_TIERS.length > 0
      ? supabase
          .from('courses')
          .select('known_lang, target_lang')
          .eq('course_code', courseCode)
          .maybeSingle()
      : Promise.resolve({ data: null, error: null }),
    // The course's RUNNING ORDER (job #949), registered in lib/runningOrder.
    // Never rejects; a course with no rows (every course but a woven one)
    // registers nothing and the walk below keeps its numeric seed order.
    ensureRunningOrder(supabase, courseCode),
  ])

  // KNOWN-side review/consolidate pull filter for this run (Tom, 2026-08-07).
  // Resolved ONCE: the mode's limit, the round it lifts at, and the one
  // resolver that knows how to count a phrase in this course's KNOWN language.
  // `countable` false ⇒ the filter is inert on this course and has already
  // warned.
  // A courses-table read error is NOT fatal — the filter going inert costs the
  // learner nothing (the character cap still applies), while throwing would
  // cost them the whole script.
  const REVIEW_FILTER_MAX_ROUND = normalizeReviewFilterMaxRound(easyOptions.reviewSyllableFilterMaxRound)
  const courseLangs = (courseRowResult?.data as { known_lang?: string; target_lang?: string } | null) || null
  if (courseRowResult?.error) {
    console.warn(`[phrase-cap] could not read courses.known_lang/target_lang for ${courseCode} — review pull filter and USE word cap may be inert:`, courseRowResult.error.message)
  }
  const knownSyllableResolver = MAX_KNOWN_SYLLABLES < Infinity
    ? makeKnownSyllableResolver(courseCode, courseLangs?.known_lang)
    : null
  const REVIEW_PULL_FILTER: ReviewPullFilter<Phrase> | null = knownSyllableResolver
    ? {
        limit: MAX_KNOWN_SYLLABLES,
        maxRound: REVIEW_FILTER_MAX_ROUND,
        syllablesOf: (p: Phrase) => knownSyllableResolver.syllablesOf(p),
      }
    : null
  const syllableCapApplied = Boolean(knownSyllableResolver?.countable)

  // SLIDING USE WORD CAP for this run (Tom, 2026-08-09). Null on Fast (no
  // ladder) and null on a course with English on neither side — the deferred
  // 47, which keep today's Easy exactly. Every application goes through
  // useWordCap() below, so the round number is never guessed.
  const USE_WORD_CAP: UseWordCap | null = makeUseWordCap(
    courseLangs?.known_lang,
    courseLangs?.target_lang,
    USE_WORD_CAP_TIERS,
  )
  const useWordCapApplied = USE_WORD_CAP !== null
  if (USE_WORD_CAP_TIERS.length > 0 && !USE_WORD_CAP) {
    console.warn(
      `[phrase-cap] USE word cap is INERT for ${courseCode}: English is on neither side ` +
      `(known '${courseLangs?.known_lang ?? '(unknown)'}', target '${courseLangs?.target_lang ?? '(unknown)'}'). ` +
      'Tom, 2026-08-09: the sliding scale was validated on English-involved pairs only; non-English pairs are deferred, not capped.',
    )
  }
  /** Apply the word cap to a USE pool for the round it will be played in. */
  const useWordCapped = (pool: readonly Phrase[], round: number): readonly Phrase[] =>
    capUsePoolByWords(pool, round, USE_WORD_CAP)

  // "No filtering on BLD phrases" (Tom, 2026-08-07) — Easy takes its BUILD
  // pool whole. Default true keeps every other caller on the historic path.
  const FILTER_BUILD_PHRASES = easyOptions.filterBuildPhrases !== false

  // All build loops below tick this — no single main-thread slice of the
  // walk may exceed the budget (post-READY interactivity, founder 2026-07-30).
  const yieldTick = makeSliceYielder()

  if (legosResult.error) throw new Error('Failed to query LEGOs: ' + legosResult.error.message)
  if (phrasesResult.error) throw new Error('Failed to query phrases: ' + phrasesResult.error.message)
  if (seedsResult.error) throw new Error('Failed to query seeds for the whole-seed visit: ' + seedsResult.error.message)
  if (bookendsResult.error) throw new Error('Failed to query listen bookends: ' + bookendsResult.error.message)
  if (podsResult.error) throw new Error('Failed to query pod sentences: ' + podsResult.error.message)

  // Stamp the revision suffix onto every audio-id column BEFORE anything below
  // reads one. Doing it here — once, on the fetched rows — rather than at the
  // dozens of places that copy an id onto a ScriptItem is the same trick the
  // server routes use (`stampRowAudioRefs` in api/_utils/audioAccess.ts), and
  // for the same reason: the ref is just a string that rides through untouched.
  // No-op (and no copy) when the course has no revised clips.
  // Per-clip versioned audio refs: this walk reads the denormalised
  // `*_audio_id` columns straight from Supabase, so — unlike the /cycles
  // routes, which run the same map server-side — nothing here would carry a
  // revision suffix without this. Looked up for exactly the ids on these rows
  // (a declared whole-course walk, so the helper asks course-wide in one query);
  // never throws. See ./revisedAudioRefs.
  //
  // Pod lines and bookends are asked BY ID first (job #338): pods are per
  // language, so their clips are often another course's rows, which the
  // course-wide query below cannot see. Asked by id they are answered whatever
  // course recorded them; the walk then skips them as already resolved.
  await getRevisedAudioRefs(
    supabase,
    courseCode,
    collectAudioIds(bookendsResult.data || [], podsResult.data || []),
  )
  const revisedAudioRefs = await getRevisedAudioRefs(
    supabase,
    courseCode,
    collectAudioIds(
      legosResult.data || [],
      phrasesResult.data || [],
      seedsResult.data || [],
      bookendsResult.data || [],
      podsResult.data || [],
    ),
    { wholeCourseWalk: true },
  )
  if (revisedAudioRefs.size > 0) {
    legosResult.data = stampRowAudioRefs(revisedAudioRefs, legosResult.data || [])
    phrasesResult.data = stampRowAudioRefs(revisedAudioRefs, phrasesResult.data || [])
    seedsResult.data = stampRowAudioRefs(revisedAudioRefs, seedsResult.data || [])
    bookendsResult.data = stampRowAudioRefs(revisedAudioRefs, bookendsResult.data || [])
    podsResult.data = stampRowAudioRefs(revisedAudioRefs, podsResult.data || [])
    console.log(`[generateLearningScript] ${revisedAudioRefs.size} revised clip(s) in ${courseCode} — audio refs stamped`)
  }

  // Map bookend role → audio (used in Phase 6 to wrap the listening batch).
  // Both intro and outro must exist for either to be emitted.
  interface BookendAudio { id: string; text: string; duration_ms?: number }
  const bookendByRole = new Map<string, BookendAudio>()
  for (const row of (bookendsResult.data || []) as Array<{ role: string; text: string; id: string; duration_ms?: number }>) {
    bookendByRole.set(row.role, { id: row.id, text: row.text, duration_ms: row.duration_ms })
  }
  const listenIntroAudio = bookendByRole.get('bookend_listen_intro')
  const listenOutroAudio = bookendByRole.get('bookend_listen_outro')
  const hasBookends = !!(listenIntroAudio && listenOutroAudio)

  // (Audio-ID fallback layer removed 2026-05-03. It scanned up to 20K rows
  // from course_audio per script generation to patch NULL audio_id columns
  // left behind by the dashboard's text-edit trigger, and was the heaviest
  // query in the trio for big courses — tipping Estonian / Basque over
  // Postgres' statement timeout. The fix lives upstream in the dashboard:
  // close the re-link gap when fresh audio lands. Until then, phrases with
  // NULL audio IDs are gracefully skipped by the downstream filters.)

  // -------------------------------------------------------------------------
  // Listening Layers.
  //
  //   Layer 2 (Pod 0):  fires every POD_ROUND_INTERVAL rounds (default 5)
  //                     from podActivationRound onward. Runtime-scheduled
  //                     by usePodLapScheduler. Pod-round counts fires,
  //                     not main-rounds. Stage table unchanged.
  //   Layer 1:          REMOVED from main flow 2026-05-19, replaced by the
  //                     runtime 30-cup wheel (useLayer1Scheduler.ts). Seed
  //                     graduation went with the SEED-PHASE review tier on
  //                     2026-09-18 — see the SPACED REP phase below.
  // -------------------------------------------------------------------------
  const POD_ACTIVATION_ROUND = listeningConfig.podActivationRound ?? 6
  const POD_ROUND_INTERVAL = Math.max(1, Math.floor(podRoundInterval))
  type PodPlayRole = 'ps08x' | 'ps' | 'ps15x' | 'ps2x' | 'trans'
  // Stage playlists per Aran's road-test 2026-05-05. PS = pod sentence at
  // 1.0×, PS×2 at 2.0×, trans = English translation. Stage 1 stays all 1.0×;
  // 2× kicks in from stage 2. Stages 1–6 each last 5 pod-rounds (was 3);
  // stage 7 is the eternal holding bay.
  const STAGE_PLAYLIST: Record<number, PodPlayRole[]> = {
    1: ['ps', 'trans', 'ps', 'ps'],
    2: ['ps', 'trans', 'ps2x', 'ps2x'],
    3: ['ps', 'trans', 'ps2x'],
    4: ['ps2x', 'trans', 'ps2x'],
    5: ['ps', 'ps2x'],
    6: ['ps2x', 'ps2x'],
    7: ['ps2x'],
  }
  const STAGE_DURATION = 5
  function podStageFor(entryPodRound: number, currentPodRound: number): { stage: number; iter: number | null } | null {
    const alive = currentPodRound - entryPodRound + 1
    if (alive < 1) return null
    for (let stage = 1; stage <= 6; stage++) {
      const stageEnd = stage * STAGE_DURATION
      if (alive <= stageEnd) {
        return { stage, iter: alive - (stage - 1) * STAGE_DURATION }
      }
    }
    return { stage: 7, iter: null }
  }
  interface PodSentenceRow {
    global_order: number
    target_text: string
    known_text: string
    target_audio_id: string | null
    known_audio_id: string | null
  }
  const podSentences = (podsResult.data || []) as PodSentenceRow[]
  const hasPods = podSentences.length > 0

  // Pod-round counts actual fires, not player rounds. With interval N, a
  // pod-lap only fires on rounds where (mainRound - activation) % N === 0;
  // the pod-round increments by 1 per fire (so stage clocks still measure
  // laps, not session-rounds). Non-firing rounds map to 0.
  function podRoundForMainRound(mainRound: number): number {
    if (mainRound < POD_ACTIVATION_ROUND) return 0
    const offset = mainRound - POD_ACTIVATION_ROUND
    if (offset % POD_ROUND_INTERVAL !== 0) return 0
    return Math.floor(offset / POD_ROUND_INTERVAL) + 1
  }
  function l2FiresAt(round: number): boolean {
    if (!hasPods || round < POD_ACTIVATION_ROUND) return false
    return (round - POD_ACTIVATION_ROUND) % POD_ROUND_INTERVAL === 0
  }

  // Compute lap items for a given main-course round. Returns false when pods
  // not activated, course has none, or the served pod has been fully introduced and
  // no sentence is in any stage (shouldn't happen since stage 7 is eternal).
  // Caller is responsible for gating on l2FiresAt(round).
  function emitPodLap(mainRoundNumber: number, cycleCounter: { v: number }): boolean {
    if (!hasPods) return false
    const podRound = podRoundForMainRound(mainRoundNumber)
    if (podRound < 1) return false
    const TOTAL = podSentences.length
    const activeCount = Math.min(podRound, TOTAL)
    if (activeCount < 1) return false

    // Pre-flight: collect plays so we can decide whether to emit bookends.
    const plays: Array<{ i: number; sentence: PodSentenceRow; playRole: PodPlayRole }> = []
    for (let i = 1; i <= activeCount; i++) {
      const sentence = podSentences[i - 1]
      if (!sentence.target_audio_id) continue
      const stageInfo = podStageFor(i, podRound)
      if (!stageInfo) continue
      for (const playRole of STAGE_PLAYLIST[stageInfo.stage]) {
        if (playRole === 'trans' && !sentence.known_audio_id) continue
        plays.push({ i, sentence, playRole })
      }
    }
    if (plays.length === 0) return false

    if (hasBookends && listenIntroAudio) {
      cycleCounter.v++
      emitItem({
        uuid: `listen_intro_pod_R${String(mainRoundNumber).padStart(4, '0')}_${cycleCounter.v}`,
        cycleNum: cycleCounter.v, roundNumber: mainRoundNumber,
        seedId: '', legoKey: '', seedCode: '', legoCode: '',
        type: 'listen_intro',
        knownText: listenIntroAudio.text,
        targetText: '',
        knownAudioId: listenIntroAudio.id,
        isNew: false,
      })
    }
    for (const { i, sentence, playRole } of plays) {
      cycleCounter.v++
      const cyc = cycleCounter.v
      const speed = ROLE_SPEED[playRole] ?? 1.0
      const isTrans = playRole === 'trans'
      emitItem({
        uuid: `pod_R${String(mainRoundNumber).padStart(4, '0')}_S${String(i).padStart(3, '0')}_${playRole}_${cyc}`,
        cycleNum: cyc, roundNumber: mainRoundNumber,
        seedId: '', legoKey: '', seedCode: '', legoCode: '',
        type: 'pod',
        knownText: isTrans ? sentence.known_text : '',
        targetText: isTrans ? '' : sentence.target_text,
        knownAudioId: isTrans ? (sentence.known_audio_id || undefined) : undefined,
        target1Id: isTrans ? undefined : (sentence.target_audio_id || undefined),
        isNew: false,
        playbackSpeed: speed,
      })
    }
    if (hasBookends && listenOutroAudio) {
      cycleCounter.v++
      emitItem({
        uuid: `listen_outro_pod_R${String(mainRoundNumber).padStart(4, '0')}_${cycleCounter.v}`,
        cycleNum: cycleCounter.v, roundNumber: mainRoundNumber,
        seedId: '', legoKey: '', seedCode: '', legoCode: '',
        type: 'listen_outro',
        knownText: listenOutroAudio.text,
        targetText: '',
        knownAudioId: listenOutroAudio.id,
        isNew: false,
      })
    }
    return true
  }

  // Seed sentences, keyed by seed number — the payload of the whole-seed visit.
  interface SeedData {
    seed_number: number
    known_text: string
    target_text: string
    target_text_roman?: string
    known_audio_id?: string
    target1_audio_id?: string
    target2_audio_id?: string
  }
  const seedMap = new Map<number, SeedData>()
  for (const seed of (seedsResult.data || []) as SeedData[]) {
    seedMap.set(seed.seed_number, seed)
  }

  // WHOLE-SEED ANCHORS: the one LEGO per seed that speaks for the whole seed
  // at offsets 89 and 144 — its LAST introduced LEGO, so the sentence lands
  // after all of its parts have had their own airings. Every other LEGO of
  // that seed emits NOTHING at those rungs, which is what keeps a four-LEGO
  // seed from coming back four rounds running (Tom, 2026-09-20).
  const seedAnchorLegos = new Set<string>()
  {
    const lastBySeed = new Map<number, string>()
    for (const row of (legosResult.data || []) as Array<{ seed_number: number; lego_index: number; is_new?: boolean }>) {
      if (row.is_new === false) continue
      const k = `S${String(row.seed_number).padStart(4, '0')}L${String(row.lego_index).padStart(2, '0')}`
      const prev = lastBySeed.get(row.seed_number)
      if (!prev || k > prev) lastBySeed.set(row.seed_number, k)
    }
    for (const k of lastBySeed.values()) seedAnchorLegos.add(k)
  }

  // FLAG: LEGOs with bracket explanations (these shouldn't exist in production)
  const bracketPattern = /\[.*?\]/
  const legosWithBrackets = (legosResult.data || []).filter(
    (l: any) => bracketPattern.test(l.known_text) || bracketPattern.test(l.target_text)
  )
  if (legosWithBrackets.length > 0) {
    console.warn(`[generateLearningScript] ${legosWithBrackets.length} LEGOs with bracket explanations`)
  }

  // Group phrases by LEGO into BUILD and USE pools
  interface Phrase {
    /** `<course>:S0042L03U05` — the phrase's own row id, the name the cycle id carries. */
    id?: string
    seed_number: number
    lego_index: number
    known_text: string
    target_text: string
    target_text_roman?: string
    phrase_role: string
    target_syllable_count?: number
    position?: number
    known_audio_id?: string
    target1_audio_id?: string
    target2_audio_id?: string
    presentation_audio_id?: string
    target1_duration_ms?: number
    target2_duration_ms?: number
    introduce?: boolean
    decomposition?: Array<{ legoId: string | null; target: string; known: string; isGhost: boolean; isSalient?: boolean }> | null
    display_tiling?: Array<{ n: string; r: string; salient?: boolean }> | null
  }
  const phrasesByLego = new Map<string, { build: Phrase[]; use: Phrase[]; practice: Phrase[] }>()
  // Collect M-LEGO component breakdowns: legoKey → [{known, target}, ...]
  const componentsByLego = new Map<string, Array<{ known: string; target: string }>>()
  const componentsByLegoNative = new Map<string, Array<{ known: string; target: string }>>()
  // Full component phrases with audio IDs for component priming
  const componentPhrasesByLego = new Map<string, Phrase[]>()
  // Same audio-completeness invariant as LEGOs: a phrase used in a cycle
  // must have all three audio IDs. Visual-only component tiles
  // (introduce === false, shown on intro cards without audio playback)
  // are exempt — they're purely presentational.
  const phraseHasFullAudio = (p: Phrase): boolean =>
    !!(p.known_audio_id && p.target1_audio_id && p.target2_audio_id)
  // Untranslatable component particles (Chinese 了/的/得 etc.) intentionally
  // have empty known_text and no known_audio_id — they're function words
  // with no English equivalent. They're skipped from audio cycles by design,
  // not because of a missing-audio bug. Don't count them in the warning.
  const isIntentionalParticleSkip = (p: Phrase): boolean =>
    p.phrase_role === 'component' && (!p.known_text || p.known_text.trim() === '')
  let phrasesSkippedForAudio = 0
  let particleSkips = 0
  for (const phrase of (phrasesResult.data || []) as Phrase[]) {
    await yieldTick()
    const key = `${phrase.seed_number}:${phrase.lego_index}`
    if (!phrasesByLego.has(key)) phrasesByLego.set(key, { build: [], use: [], practice: [] })
    const group = phrasesByLego.get(key)!
    if (phrase.phrase_role === 'component') {
      // Visual tiles on intro/debut — ALL components (even introduce=false)
      if (!componentsByLego.has(key)) componentsByLego.set(key, [])
      componentsByLego.get(key)!.push({ known: phrase.known_text, target: phrase.target_text_roman || phrase.target_text })
      // Store native script variant when romanized exists
      if (phrase.target_text_roman) {
        if (!componentsByLegoNative.has(key)) componentsByLegoNative.set(key, [])
        componentsByLegoNative.get(key)!.push({ known: phrase.known_text, target: phrase.target_text })
      }
      // Audio cycles (component_intro/component_practice) — only introduced components with full audio
      if (phrase.introduce !== false) {
        if (!phraseHasFullAudio(phrase)) {
          if (isIntentionalParticleSkip(phrase)) particleSkips++
          else phrasesSkippedForAudio++
          continue
        }
        if (!componentPhrasesByLego.has(key)) componentPhrasesByLego.set(key, [])
        componentPhrasesByLego.get(key)!.push(phrase)
      }
      continue
    }
    if (!phraseHasFullAudio(phrase)) {
      phrasesSkippedForAudio++
      continue
    }
    if (phrase.phrase_role === 'build') group.build.push(phrase)
    else if (phrase.phrase_role === 'use') group.use.push(phrase)
    else if (phrase.phrase_role === 'practice') group.practice.push(phrase)
  }
  if (phrasesSkippedForAudio > 0) {
    console.warn(`[generateLearningScript] Skipped ${phrasesSkippedForAudio} practice phrases for "${courseCode}" (missing audio IDs)`)
  }
  if (particleSkips > 0) {
    console.debug(`[generateLearningScript] Skipped ${particleSkips} untranslatable particles for "${courseCode}" (intentional)`)
  }

  console.log(`[generateLearningScript] ${phrasesResult.data?.length || 0} phrases fetched, ${componentsByLego.size} LEGOs with components`)

  // Classify legacy 'practice' phrases per LEGO:
  // - If the LEGO already has explicit USE phrases, practice → BUILD (fragments, drill once)
  // - If the LEGO has NO USE phrases, practice → USE (so it has spaced rep material)
  for (const [, group] of phrasesByLego.entries()) {
    if (group.practice.length === 0) continue
    if (group.use.length > 0) {
      group.build.push(...group.practice)
    } else {
      group.use.push(...group.practice)
    }
    group.practice = []
  }

  // Sort BUILD phrases shortest-first and apply the mode's phrase-length cap
  // — the twin of the USE sort below; both must move together or a round's
  // BUILD and USE halves disagree about length. capPhrasesByLength falls back
  // to that LEGO's shortest phrases rather than starve the round. The floor is
  // the methodology's per-LEGO minimum (4 BUILD / 5 USE), NOT the round's
  // ceiling: passing the ceiling makes the guard swallow the cap on every LEGO
  // smaller than it, which is most of them (BUILD pools average 3.2 phrases).
  // One ceiling for the whole run, from every pool the learner can meet.
  const PHRASE_LENGTH_LIMIT = PHRASE_LENGTH_FRACTION >= 1
    ? Infinity
    : courseMaxPhraseLength<Phrase>(
        [...phrasesByLego.values()].flatMap((g) => [g.build, g.use]),
        phraseLengthOf,
      ) * PHRASE_LENGTH_FRACTION

  // Easy passes filterBuildPhrases:false and so keeps its whole BUILD pool,
  // shortest-first but uncut — "no filtering on BLD phrases" (Tom,
  // 2026-08-07). Passing Infinity rather than branching keeps the sort in one
  // place: capPhrasesByLength with no finite limit IS the plain historic sort.
  for (const [, group] of phrasesByLego.entries()) {
    group.build = capPhrasesByLength<Phrase>(
      group.build,
      phraseSyllables,
      phraseLengthOf,
      FILTER_BUILD_PHRASES ? PHRASE_LENGTH_LIMIT : Infinity,
      MIN_BUILD_PHRASES_AFTER_CAP,
    )
  }

  // Organize LEGOs by seed
  interface Lego {
    seed_number: number
    lego_index: number
    known_text: string
    target_text: string
    target_text_roman?: string
    type: string
    is_new: boolean
    known_audio_id?: string
    target1_audio_id?: string
    target2_audio_id?: string
    presentation_audio_id?: string
    target1_duration_ms?: number
    target2_duration_ms?: number
  }
  const allLegosRaw = (legosResult.data || []) as Lego[]
  // Invariant (unchanged): never SCHEDULE an unplayable cycle. Partial-import
  // courses (e.g. Greek 2026-04) had LEGOs with NULL target audio, which
  // caused silent play.
  //
  // What changed 2026-08-06 is the GRANULARITY. This used to drop the whole
  // LEGO from the walk, which cost far more than the missing clip: round
  // numbers are assigned after this point, so one gap slid every later round
  // down by one and re-paired the entire Fibonacci review schedule
  // (fra_for_eng, from round 47), and a partial import amputated the course
  // outright (ara_lb_for_eng lost 776 of 1414 rounds — the player simply
  // stopped). A course must always play what it HAS.
  //
  // So every LEGO now keeps its round and its round NUMBER and enters
  // legoState for later review; the audio invariant is enforced per ITEM at
  // emit time (introIsPlayable / debutIsPlayable below), exactly as phrase
  // rows already are. A round that loses its intro/debut keeps its build,
  // use, review and consolidate cycles. If a round ends up with nothing
  // playable at all, toSimpleRounds drops it (its `cycles.length === 0`
  // guard) and SimplePlayer — which walks rounds by array index, not by
  // round number — carries on; it also advances defensively on an empty
  // round, so a hole can't stall playback.
  const allLegos = allLegosRaw

  // Backfill missing presentation_audio_id from course_audio / lego_introductions
  // Some courses have presentation audio generated but not yet linked to course_legos
  const legosMissingPresentation = allLegos.filter(l => l.is_new && !l.presentation_audio_id)
  if (legosMissingPresentation.length > 0) {
    const missingLegoIds = legosMissingPresentation.map(l =>
      `S${String(l.seed_number).padStart(4, '0')}L${String(l.lego_index).padStart(2, '0')}`
    )

    // Try course_audio first (authoritative), then lego_introductions (legacy)
    const [courseAudioResult, introResult] = await Promise.all([
      supabase
        .from('course_audio')
        .select('id, lego_id, s3_key, created_at')
        .eq('course_code', courseCode)
        .eq('role', 'presentation')
        .in('lego_id', missingLegoIds)
        // Oldest first, so the loop below (which overwrites) lands on the
        // NEWEST row. One lego_id can carry a superseded presentation clip and
        // its replacement (canon C23, 2026-08-27); pending rows are skipped
        // outright because they name audio that has not been rendered yet.
        .order('created_at', { ascending: true }),
      supabase
        .from('lego_introductions')
        .select('lego_id, presentation_audio_id, audio_uuid')
        .eq('course_code', courseCode)
        .in('lego_id', missingLegoIds)
    ])

    // Build lookup: lego_id → audio ID (prefer course_audio.id, fallback to lego_introductions)
    const presLookup = new Map<string, string>()
    // THE LEGACY IDS ARE NOT TRUSTED (job #256, 2026-09-19). A
    // `lego_introductions` row is a 2025 import and nothing keeps it honest:
    // cym_s_for_eng's three unlinked LEGOs — S0006L01, S0041L01, S0154L02 —
    // each name an `audio_uuid` that exists in NO audio table, so the prompt
    // url 404s, the element refuses the JSON body with
    // MEDIA_ERR_SRC_NOT_SUPPORTED, and the intro dies. 96 failures in one week
    // on S0006L01 alone. A course_audio row IS its own proof of existence; a
    // legacy id has to be checked, which is one extra `in.(...)` over a
    // handful of ids and only when a LEGO is missing its link at all.
    const legacyIds = Array.from(new Set(
      ((introResult.data || []) as any[])
        .map((row) => row.presentation_audio_id || row.audio_uuid)
        .filter(Boolean)
        .map(String),
    ))
    let resolvableLegacyIds = new Set<string>()
    if (legacyIds.length > 0) {
      const { data: legacyRows } = await supabase
        .from('course_audio')
        .select('id')
        .in('id', legacyIds)
      resolvableLegacyIds = new Set(((legacyRows || []) as any[]).map((r) => String(r.id)))
      const dangling = legacyIds.length - resolvableLegacyIds.size
      if (dangling > 0) {
        console.warn(
          `[generateLearningScript] ${dangling}/${legacyIds.length} legacy lego_introductions ` +
          `audio ids name audio that does not exist — ignoring them; those intros use the ` +
          `known clip as their prompt`,
        )
      }
    }
    for (const row of (introResult.data || []) as any[]) {
      const audioId = row.presentation_audio_id || row.audio_uuid
      if (audioId && resolvableLegacyIds.has(String(audioId))) presLookup.set(row.lego_id, String(audioId))
    }
    for (const row of (courseAudioResult.data || []) as any[]) {
      if (row.s3_key && String(row.s3_key).startsWith('pending/')) continue
      if (row.id && row.lego_id) presLookup.set(row.lego_id, row.id)  // overwrites legacy, newest wins
    }

    if (presLookup.size > 0) {
      console.debug(`[generateLearningScript] Backfilled ${presLookup.size}/${legosMissingPresentation.length} missing presentation audio IDs`)
      // These ids were not on the rows the bulk pass looked up, so top the
      // shared map up with exactly them before stamping.
      const presRefs = await getRevisedAudioRefs(supabase, courseCode, presLookup.values())
      for (const lego of legosMissingPresentation) {
        const legoId = `S${String(lego.seed_number).padStart(4, '0')}L${String(lego.lego_index).padStart(2, '0')}`
        // Stamped on the way in: this lookup was built from queries issued
        // AFTER the bulk stamping pass above, so its ids are still bare.
        const audioId = applyAudioRef(presRefs, presLookup.get(legoId))
        if (audioId) lego.presentation_audio_id = audioId
      }
    } else if (legosMissingPresentation.length > 0) {
      console.warn(`[generateLearningScript] ${legosMissingPresentation.length} LEGOs missing presentation audio (not in course_audio or lego_introductions)`)
    }
  }

  const legosBySeed = new Map<number, Lego[]>()
  for (const lego of allLegos) {
    if (!legosBySeed.has(lego.seed_number)) legosBySeed.set(lego.seed_number, [])
    legosBySeed.get(lego.seed_number)!.push(lego)
  }

  // Diagnostic: report what was loaded. Per-item audio suppression is counted
  // during the walk and reported after it (introsSkippedForAudio /
  // debutsSkippedForAudio) — a LEGO short of one clip no longer costs a round.
  if (allLegosRaw.length === 0) {
    console.warn(`[generateLearningScript] No LEGOs found for course "${courseCode}"`)
  }
  let introsSkippedForAudio = 0
  let debutsSkippedForAudio = 0

  // The walk's seed order. A woven course (one with a course_running_order)
  // walks by POSITION and skips seeds dropped from its running order; every
  // other course gets exactly the numeric sort it always had.
  const sortedSeedNums = orderSeedNumbers(courseCode, legosBySeed.keys())
  interface LegoState {
    lastRound: number
    usePhrases: Phrase[]
    useIndex: number
    seedNum: number
    legoIndex: number
    lego: Lego
  }
  const legoState = new Map<string, LegoState>()
  const items: ScriptItem[] = []
  let cycleNum = 0
  let roundNumber = 0

  // Build LEGO text map for phrase decomposition (normalised target text → LEGO key)
  // Uses ALL LEGOs (not just is_new) since reused LEGOs are still valid vocabulary
  const legoTextMap = new Map<string, string>()
  for (const lego of allLegos) {
    const legoKey = `S${String(lego.seed_number).padStart(4, '0')}L${String(lego.lego_index).padStart(2, '0')}`
    const normalized = normalizeText(lego.target_text)
    if (normalized) legoTextMap.set(normalized, legoKey)
    // Also index by romanized text so phrases using target_text_roman can decompose
    if (lego.target_text_roman) {
      const normalizedRoman = normalizeText(lego.target_text_roman)
      if (normalizedRoman && !legoTextMap.has(normalizedRoman)) {
        legoTextMap.set(normalizedRoman, legoKey)
      }
    }
  }

  // Reverse map: LEGO key → display text (prefer romanized for display when available)
  const legoIdToText = new Map<string, string>()
  // Native script map: LEGO key → native text (only populated when romanized exists)
  const legoIdToTextNative = new Map<string, string>()
  for (const lego of allLegos) {
    const legoKey = `S${String(lego.seed_number).padStart(4, '0')}L${String(lego.lego_index).padStart(2, '0')}`
    if (lego.target_text) legoIdToText.set(legoKey, lego.target_text_roman || lego.target_text)
    if (lego.target_text_roman) legoIdToTextNative.set(legoKey, lego.target_text)
  }

  // Greedy longest-match decomposition of a phrase into component LEGO IDs
  // Supports both space-separated languages and CJK (no spaces)
  const cjkRegex = /[\u3000-\u9fff\uac00-\ud7af\uff00-\uffef]/
  const isCJK = (text: string) => cjkRegex.test(text)

  // Track synthetic (on-the-fly) LEGOs for unmatched text
  let syntheticCounter = 0
  const syntheticLegoMap = new Map<string, string>() // normalized text → synthetic ID

  const getOrCreateSyntheticLego = (text: string): string => {
    const existing = syntheticLegoMap.get(text)
    if (existing) return existing
    const id = `_SYN${String(++syntheticCounter).padStart(4, '0')}`
    syntheticLegoMap.set(text, id)
    legoIdToText.set(id, text)
    return id
  }

  const decomposePhrase = (targetText: string): string[] => {
    const normalized = normalizeText(targetText)
    if (!normalized) return []

    // CJK: character-level sliding window (no spaces to split on)
    if (isCJK(normalized)) {
      const chars = [...normalized] // proper Unicode split
      const result: string[] = []
      let i = 0
      while (i < chars.length) {
        let longestMatch: string | null = null
        let longestLength = 0
        for (let len = chars.length - i; len > 0; len--) {
          const candidate = chars.slice(i, i + len).join('')
          const legoId = legoTextMap.get(candidate)
          if (legoId) {
            longestMatch = legoId
            longestLength = len
            break
          }
        }
        if (longestMatch) {
          result.push(longestMatch)
          i += longestLength
        } else {
          // Create synthetic LEGO for this character
          const char = chars[i]
          result.push(getOrCreateSyntheticLego(char))
          i++
        }
      }
      return result
    }

    // Space-separated languages: word-level sliding window
    const words = normalized.split(/\s+/).filter(w => w.length > 0)
    const result: string[] = []
    let i = 0
    while (i < words.length) {
      let longestMatch: string | null = null
      let longestLength = 0
      for (let len = words.length - i; len > 0; len--) {
        const candidate = words.slice(i, i + len).join(' ')
        const legoId = legoTextMap.get(candidate)
        if (legoId) {
          longestMatch = legoId
          longestLength = len
          break
        }
      }
      if (longestMatch) {
        result.push(longestMatch)
        i += longestLength
      } else {
        // Create synthetic LEGO for this word
        result.push(getOrCreateSyntheticLego(words[i]))
        i++
      }
    }
    return result
  }

  // Track intros missing presentation audio (logged as summary at end, not per-item)
  const introsMissingAudio: string[] = []

  // Always-true emit gate. The full-course refactor drops emit windowing —
  // the script generator is now one-shot whole-course. The player handles
  // resume-from-position via cursor jumps, not by skipping early items in
  // the script. Helper retained as a no-op for now so the in-loop call
  // sites below don't need editing in this pass.
  const shouldEmit = () => true
  // Flipped to true the moment the main loop ends (see mainLoopLastRound):
  // every item emitted after that is a revival-tail item.
  let inRevivalTail = false
  const emitItem = (item: ScriptItem) => {
    item.revival = inRevivalTail
    if (item.type === 'intro' || item.type === 'component_intro') {
      // Intros ALWAYS pass — they define the round structure.
      // Missing presentation audio is handled by SimplePlayer (skips empty prompt phase).
      // Target voice1/voice2 still play to introduce the LEGO pronunciation.
      if (!item.presentationAudioId && !item.knownAudioId && item.type === 'intro') {
        introsMissingAudio.push(item.legoKey || 'unknown')
      }
    } else if (item.type === 'listening') {
      // Listening items only need target audio (passive listening, no known prompt)
      if (!item.target1Id) return
    } else if (item.type === 'listen_intro' || item.type === 'listen_outro') {
      // Bookends play one known-language clip — no target voices, no pause.
      // The audio is stored under knownAudioId so SimplePlayer's prompt phase
      // picks it up; voice1/voice2 are intentionally absent.
      if (!item.knownAudioId) return
    } else if (item.type === 'pod') {
      // Pod plays carry exactly one of {knownAudioId (translation play),
      // target1Id (sentence play, possibly with playbackSpeed=2.0)}. Never
      // both, never target2Id. The "all three audio IDs" check below would
      // wrongly drop every pod item, leaving the round-end lap empty.
      if (!item.knownAudioId && !item.target1Id) return
    } else {
      // Non-intro items need all three audio IDs to be useful
      if (!item.knownAudioId || !item.target1Id || !item.target2Id) return
    }
    items.push(item)
  }

  // Whether this course has any romanized text (for toggle detection)
  const courseHasRomanized = legoIdToTextNative.size > 0

  // Helper: returns native text fields when romanized text exists
  // Spread onto every emitted item. Carries the native-script variant AND, for
  // phrase-sourced items, the authoritative content-level tiling served verbatim
  // on course_practice_phrases.decomposition (LEGO/seed callers lack the field,
  // so it's simply omitted there). The player renders it directly when present.
  const nativeFields = (item: {
    target_text?: string
    target_text_roman?: string
    decomposition?: Array<{ legoId: string | null; target: string; known: string; isGhost: boolean; isSalient?: boolean }> | null
    display_tiling?: Array<{ n: string; r: string; salient?: boolean }> | null
  }) => ({
    ...(item.target_text_roman ? { targetTextNative: item.target_text } : {}),
    ...(Array.isArray(item.decomposition) && item.decomposition.length > 0
      ? { decomposition: item.decomposition }
      : {}),
    ...(Array.isArray(item.display_tiling) && item.display_tiling.length > 0
      ? { displayTiling: item.display_tiling }
      : {}),
  })

  /**
   * WHOLE-SEED visit (offsets 89 and 144) — the full parent seed sentence as
   * an ORDINARY four-phase production cycle: known prompt, computed gap,
   * target voice 1, target voice 2. One item, not four; nothing partial ever
   * reaches a round (Tom's round invariant, 2026-09-20).
   *
   * Returns false when the seed row is missing or lacks any of its three
   * clips — the caller then falls back to the ordinary use-phrase review, so
   * an incomplete seed costs the learner a review rather than shipping a
   * half-cycle.
   */
  const emitSeedVisit = (
    seedNumber: number,
    base: {
      reviewKey: string; reviewSeedId: string; reviewLegoNum: string
      roundNumber: number; fibPosition: number; reviewOf: number; uuid: string
      usedPhrasesThisRound: Set<string>
    },
  ): boolean => {
    const seed = seedMap.get(seedNumber)
    if (!seed || !seed.known_audio_id || !seed.target1_audio_id || !seed.target2_audio_id) return false
    const phraseId = getPhraseId(seed.known_text, seed.target_text)
    if (base.usedPhrasesThisRound.has(phraseId)) return false
    base.usedPhrasesThisRound.add(phraseId)
    cycleNum++
    emitItem({
      uuid: base.uuid,
      cycleNum, roundNumber: base.roundNumber, seedId: base.reviewSeedId, legoKey: base.reviewKey,
      seedCode: base.reviewSeedId, legoCode: base.reviewLegoNum,
      type: 'spaced_rep',
      reviewItemKind: 'seed',
      knownText: seed.known_text,
      targetText: seed.target_text_roman || seed.target_text,
      ...nativeFields(seed),
      knownAudioId: seed.known_audio_id,
      target1Id: seed.target1_audio_id,
      target2Id: seed.target2_audio_id,
      isNew: false,
      fibPosition: base.fibPosition,
      reviewOf: base.reviewOf,
    })
    return true
  }

  // SEED-SENTENCE REVIEW (job #495) — per-course, off for every course not in
  // @ssi/core's SEED_SENTENCE_REVIEW_COURSES, and when off NOTHING below runs:
  // `sentencesByHostRound` stays empty, so no round gains an item and the
  // script is the one this walk always produced. When on, each seed that
  // introduces no new LEGO is hosted by the round of the last new LEGO before
  // it in walk order (the round numbers are the walk's own: one per new LEGO,
  // counted exactly as the loop below counts them).
  const SEED_SENTENCES_ON = seedSentenceReviewEnabled(courseCode)
  let sentencesByHostRound = new Map<number, number[]>()
  const sentenceSeedsMissingClips = new Set<number>()
  let sentenceSeedsUnhosted = 0
  if (SEED_SENTENCES_ON) {
    // The hosts follow THE WALK'S OWN seed order, `sortedSeedNums`, so they
    // can never disagree with the round numbers the loop below hands out —
    // including after job #949 turns that order into a woven course's running
    // order. A seed with no course_legos rows at all is not in that order; it
    // is slotted in by seed number while the walk is in seed-number order,
    // and left out (counted as unhosted) once it is not, because then its
    // place is unknown here. The cycles endpoint hosts it from
    // course_running_order directly.
    const walkIsNumeric = sortedSeedNums.every((n, i) => i === 0 || sortedSeedNums[i - 1] < n)
    const lonely = [...seedMap.keys()].filter((n) => !legosBySeed.has(n))
    const sentenceWalkSeeds = walkIsNumeric
      ? [...sortedSeedNums, ...lonely].sort((a, b) => a - b)
      : sortedSeedNums
    if (!walkIsNumeric) sentenceSeedsUnhosted += lonely.length
    let r = 0
    const walk = sentenceWalkSeeds.map((seedNumber) => {
      const newCount = (legosBySeed.get(seedNumber) ?? []).filter((l) => l.is_new).length
      r += newCount
      return { seedNumber, lastNewRound: newCount > 0 ? r : null }
    })
    const { hosts, unhosted } = assignSeedSentenceHosts(walk)
    sentenceSeedsUnhosted += unhosted.length
    sentencesByHostRound = seedsByHostRound(hosts)
  }

  /**
   * One seed-sentence cycle: the seed's own three clips, or nothing. A seed
   * short of a clip is skipped silently and counted (reported once, below).
   * `offset` 0 is the first play; any other is a review `offset` rounds after
   * the host round.
   */
  const emitSeedSentence = (
    seedNumber: number,
    at: { roundNumber: number; cycleNum: number; offset: number; offsetIndex?: number; hostRound: number; usedPhrasesThisRound: Set<string> },
  ): boolean => {
    const seed = seedMap.get(seedNumber)
    if (!seed || !seedHasAllClips(seed)) {
      sentenceSeedsMissingClips.add(seedNumber)
      return false
    }
    const phraseId = getPhraseId(seed.known_text, seed.target_text)
    if (at.usedPhrasesThisRound.has(phraseId)) return false
    at.usedPhrasesThisRound.add(phraseId)
    const seedId = `S${String(seedNumber).padStart(4, '0')}`
    emitItem({
      uuid: seedSentenceCycleId(seedNumber, at.offset),
      cycleNum: at.cycleNum, roundNumber: at.roundNumber,
      seedId, legoKey: '', seedCode: seedId, legoCode: '',
      type: 'spaced_rep',
      reviewItemKind: SEED_SENTENCE_KIND,
      knownText: seed.known_text,
      targetText: seed.target_text_roman || seed.target_text,
      ...nativeFields(seed),
      knownAudioId: seed.known_audio_id,
      target1Id: seed.target1_audio_id,
      target2Id: seed.target2_audio_id,
      isNew: false,
      ...(at.offsetIndex !== undefined ? { fibPosition: at.offsetIndex } : {}),
      reviewOf: at.hostRound,
    })
    return true
  }

  // Process each seed
  for (const seedNum of sortedSeedNums) {
    // Only process LEGOs that are NEW (is_new = true)
    // LEGOs with is_new = false were already introduced in earlier seeds
    const seedLegos = legosBySeed.get(seedNum)!
      .filter(l => l.is_new)
      .sort((a, b) => a.lego_index - b.lego_index)

    for (const lego of seedLegos) {
      await yieldTick()
      roundNumber++
      const legoKey = `S${String(seedNum).padStart(4, '0')}L${String(lego.lego_index).padStart(2, '0')}`
      const seedId = `S${String(seedNum).padStart(4, '0')}`
      const legoNum = String(lego.lego_index).padStart(2, '0')
      const phraseKey = `${seedNum}:${lego.lego_index}`
      const phrases = phrasesByLego.get(phraseKey) || { build: [], use: [] }
      // presentation_audio_id comes directly from course_legos (or backfilled above)
      const presentationAudioId = lego.presentation_audio_id
      // Fallback: if no presentation audio, use known_audio_id so the intro still plays
      // the LEGO itself (known → target1 → target2, no pause) — learner hears it passively
      // before the debut asks them to produce it.
      const introAudioId = presentationAudioId || lego.known_audio_id

      const usedPhrasesThisRound = new Set<string>()
      const legoComponents = componentsByLego.get(phraseKey)
      const legoComponentsNative = componentsByLegoNative.get(phraseKey)
      // The authored mapping, when a human has segmented this LEGO and the
      // segmentation still covers the target text. Preferred by the tile
      // assembler; componentisation above stays the fallback.
      const legoGlossSegments = authoredGlossSegments(lego)

      // Per-ITEM audio invariant (see the allLegos comment above). The intro
      // plays prompt → target1 → target2 with no pause, so it needs a prompt
      // clip (presentation, or known as the documented fallback) and the first
      // target voice; a missing second voice is a phase SimplePlayer skips
      // gracefully, as bookends already rely on. The debut asks the learner to
      // produce, so it needs all three — the same rule toSimpleRounds applies
      // to every non-intro cycle, applied here so we never schedule one it
      // would only drop.
      const introIsPlayable = !!(introAudioId && lego.target1_audio_id)
      const debutIsPlayable = !!(lego.known_audio_id && lego.target1_audio_id && lego.target2_audio_id)
      if (!introIsPlayable) introsSkippedForAudio++
      if (!debutIsPlayable) debutsSkippedForAudio++

      // Phase 1: INTRO
      // The M-LEGO is the cognitive unit. For M-LEGOs the per-component breakdown
      // is rendered as ghost text under each target word (visual scaffolding) — we
      // do NOT pre-introduce components with their own audio cycles. A-LEGOs just
      // get a standard intro.
      if (introIsPlayable) {
        cycleNum++
        emitItem({
          uuid: `${legoKey}_intro_${cycleNum}`,
          cycleNum, roundNumber, seedId, legoKey,
          seedCode: seedId, legoCode: legoNum,
          type: 'intro',
          knownText: lego.known_text,
          targetText: lego.target_text_roman || lego.target_text,
          ...nativeFields(lego),
          presentationAudioId: introAudioId,
          target1Id: lego.target1_audio_id,
          target2Id: lego.target2_audio_id,
          target1DurationMs: lego.target1_duration_ms,
          target2DurationMs: lego.target2_duration_ms,
          isNew: true,
          ...(legoComponents ? { components: legoComponents } : {}),
          ...(legoComponentsNative ? { componentsNative: legoComponentsNative } : {}),
          ...(legoGlossSegments ? { glossSegments: legoGlossSegments } : {}),
        })
      }

      // Phase 2: DEBUT
      if (debutIsPlayable) {
        cycleNum++
        emitItem({
          uuid: `${legoKey}_debut_${cycleNum}`,
          cycleNum, roundNumber, seedId, legoKey,
          seedCode: seedId, legoCode: legoNum,
          type: 'debut',
          knownText: lego.known_text,
          targetText: lego.target_text_roman || lego.target_text,
          ...nativeFields(lego),
          knownAudioId: lego.known_audio_id,
          target1Id: lego.target1_audio_id,
          target2Id: lego.target2_audio_id,
          target1DurationMs: lego.target1_duration_ms,
          target2DurationMs: lego.target2_duration_ms,
          isNew: true,
          ...(legoComponents ? { components: legoComponents } : {}),
          ...(legoComponentsNative ? { componentsNative: legoComponentsNative } : {}),
          ...(legoGlossSegments ? { glossSegments: legoGlossSegments } : {}),
        })
      }
      // The debut IS the bare LEGO — claim it so no later phase replays it. Some
      // courses carry a build row whose text equals its own LEGO (deu_for_eng
      // S0001L01 'I want / ich will'); playing that as a BUILD breaks the rule that
      // a BUILD is the new LEGO plugged into ALREADY-KNOWN vocabulary, and burns a
      // build slot a real phrase should have had. Mirrors the dashboard generator
      // (services/learning-script-generator.cjs).
      usedPhrasesThisRound.add(getPhraseId(lego.known_text, lego.target_text))

      // Phase 3: BUILD phrases up to 7
      let practiceCount = 0
      const usedForPractice = new Set<string>()

      for (const phrase of phrases.build) {
        if (practiceCount >= MAX_BUILD_PHRASES) break
        const phraseId = getPhraseId(phrase.known_text, phrase.target_text)
        // Skip BEFORE consuming a build slot, so a skipped row costs nothing.
        if (usedPhrasesThisRound.has(phraseId)) continue
        cycleNum++
        practiceCount++
        usedPhrasesThisRound.add(phraseId)
        emitItem({
          uuid: phraseCycleId(legoKey, phrase.id, 'build', cycleNum),
          cycleNum, roundNumber, seedId, legoKey,
          seedCode: seedId, legoCode: legoNum,
          type: 'build',
          knownText: phrase.known_text,
          targetText: phrase.target_text_roman || phrase.target_text,
          ...nativeFields(phrase),
          knownAudioId: phrase.known_audio_id,
          target1Id: phrase.target1_audio_id,
          target2Id: phrase.target2_audio_id,
          target1DurationMs: phrase.target1_duration_ms,
          target2DurationMs: phrase.target2_duration_ms,
          isNew: true,
          syllableCount: phrase.target_syllable_count || countTargetSyllables(phrase.target_text),
        })
      }

      // Fill remaining BUILD slots with USE phrases (BUILD priority > CONSOLIDATE)
      // CONSOLIDATE can repeat BUILD phrases if needed — filling 7 BUILD is non-negotiable
      // Twin of the BUILD sort above — same shortest-first order, same cap.
      // 'needed' is only the slots still unfilled, so the starvation guard
      // measures against what this round actually has to place.
      // …then the sliding ENGLISH-side word cap for THIS round (Tom,
      // 2026-08-09): 8 words to round 20, 10 to 100, uncapped after, and off
      // entirely on a course with English on neither side. Its starvation guard
      // hands the whole pool back rather than leave the round short.
      const sortedUsePhrases = useWordCapped(capPhrasesByLength<Phrase>(
        phrases.use,
        phraseSyllables,
        phraseLengthOf,
        PHRASE_LENGTH_LIMIT,
        MIN_USE_PHRASES_AFTER_CAP,
      ), roundNumber)
      for (const phrase of sortedUsePhrases) {
        if (practiceCount >= MAX_BUILD_PHRASES) break
        const phraseId = getPhraseId(phrase.known_text, phrase.target_text)
        if (usedPhrasesThisRound.has(phraseId)) continue

        cycleNum++
        practiceCount++
        usedPhrasesThisRound.add(phraseId)
        usedForPractice.add(phraseId)
        emitItem({
          uuid: phraseCycleId(legoKey, phrase.id, 'build', cycleNum),
          cycleNum, roundNumber, seedId, legoKey,
          seedCode: seedId, legoCode: legoNum,
          type: 'build',
          knownText: phrase.known_text,
          targetText: phrase.target_text_roman || phrase.target_text,
          ...nativeFields(phrase),
          knownAudioId: phrase.known_audio_id,
          target1Id: phrase.target1_audio_id,
          target2Id: phrase.target2_audio_id,
          target1DurationMs: phrase.target1_duration_ms,
          target2DurationMs: phrase.target2_duration_ms,
          isNew: true,
          syllableCount: phrase.target_syllable_count || countTargetSyllables(phrase.target_text),
        })
      }

      // Initialize LEGO state.
      // The review pool gets the SAME cap. It has to: spaced rep walks this
      // pool round-robin (useIndex % length), so it is the only place a LEGO's
      // LONGEST phrases ever reach the learner — the debut BUILD/USE fills
      // above take a shortest-first prefix and would ignore a cap entirely.
      // Guard against an N-1 review's worth, the largest single draw.
      legoState.set(legoKey, {
        lastRound: roundNumber,
        usePhrases: capPhrasesByLength<Phrase>(
          phrases.use,
          phraseSyllables,
          phraseLengthOf,
          PHRASE_LENGTH_LIMIT,
          MIN_USE_PHRASES_AFTER_CAP,
        ),
        useIndex: 0,
        seedNum, legoIndex: lego.lego_index, lego
      })

      // Phase 4: SPACED REP
      const dueForReview: { key: string; state: LegoState; fibPosition: number; phraseCount: number }[] = []
      const seenLegos = new Set<string>()

      for (let offsetIdx = 0; offsetIdx < SPACED_REP_OFFSETS.length; offsetIdx++) {
        const offset = SPACED_REP_OFFSETS[offsetIdx]
        const reviewRound = roundNumber - offset
        if (reviewRound < 1) break

        for (const [prevKey, state] of legoState.entries()) {
          if (prevKey === legoKey || seenLegos.has(prevKey)) continue
          if (state.lastRound === reviewRound) {
            const isN1 = offset === 1
            const phraseCount = isN1 ? N1_PHRASE_COUNT : 1
            dueForReview.push({ key: prevKey, state, fibPosition: offsetIdx, phraseCount })
            seenLegos.add(prevKey)
          }
        }
      }

      let spacedRepCount = 0
      for (const { key: reviewKey, state, fibPosition, phraseCount } of dueForReview) {
        // The two WHOLE-SEED rungs sit OUTSIDE MAX_SPACED_REP_PHRASES. They
        // come last in offset order and the eleven use-phrase reviews ahead of
        // them fill the 12-slot budget exactly, so budgeting them meant rung
        // 144 fired for barely half the seeds — measured on ita_for_eng: 893
        // visits across 598 seeds, where 1,196 was the intended number.
        const isSeedRung = reviewItemIsSeed(SPACED_REP_OFFSETS[fibPosition])
        if (!isSeedRung && spacedRepCount >= MAX_SPACED_REP_PHRASES) break

        const reviewLegoNum = reviewKey.match(/L(\d+)/)?.[1] || ''
        const reviewSeedId = reviewKey.match(/S\d+/)?.[0] || ''

        // WHOLE-SEED rungs (89, 144): the parent sentence, once per seed.
        // Only the seed's anchor LEGO speaks for it; the others emit nothing.
        if (isSeedRung) {
          if (!seedAnchorLegos.has(reviewKey)) continue
          const emitted = emitSeedVisit(state.seedNum, {
            reviewKey, reviewSeedId, reviewLegoNum, roundNumber, fibPosition,
            reviewOf: state.lastRound, usedPhrasesThisRound,
            uuid: `${reviewKey}_seedrep_${SPACED_REP_OFFSETS[fibPosition]}`,
          })
          if (emitted) { spacedRepCount++; continue }
          // seed missing / incomplete audio → fall through to the use-phrase
          // review, which IS budgeted like any other.
          if (spacedRepCount >= MAX_SPACED_REP_PHRASES) continue
        }

        if (state.usePhrases.length === 0) continue

        // KNOWN-side pull filter (Tom, 2026-08-07): for the first
        // REVIEW_FILTER_MAX_ROUND rounds a review draws from the shorter half
        // of the LEGO's basket, measured in the learner's OWN language. Past
        // that round the basket opens fully — "it's the LEGO that you are
        // practicing", so a phrase never met before is no obstacle. Rotation
        // still walks useIndex round-robin, just over the eligible sub-basket.
        const reviewPool = useWordCapped(filterReviewPool(state.usePhrases, roundNumber, REVIEW_PULL_FILTER), roundNumber)
        const phrasesToUse = Math.min(phraseCount, MAX_SPACED_REP_PHRASES - spacedRepCount, reviewPool.length)
        for (let i = 0; i < phrasesToUse; i++) {
          const phrase = reviewPool[state.useIndex % reviewPool.length]
          state.useIndex++

          const phraseId = getPhraseId(phrase.known_text, phrase.target_text)
          if (usedPhrasesThisRound.has(phraseId)) continue
          usedPhrasesThisRound.add(phraseId)

          cycleNum++
          spacedRepCount++
          emitItem({
            uuid: phraseCycleId(reviewKey, phrase.id, 'review', cycleNum),
            cycleNum, roundNumber, seedId: reviewSeedId, legoKey: reviewKey,
            seedCode: reviewSeedId, legoCode: reviewLegoNum,
            type: 'spaced_rep',
            knownText: phrase.known_text,
            targetText: phrase.target_text_roman || phrase.target_text,
            ...nativeFields(phrase),
            knownAudioId: phrase.known_audio_id,
            target1Id: phrase.target1_audio_id,
            target2Id: phrase.target2_audio_id,
            target1DurationMs: phrase.target1_duration_ms,
            target2DurationMs: phrase.target2_duration_ms,
            isNew: false,
            fibPosition,
            reviewOf: state.lastRound,
          })
        }
      }

      // Seed-sentence reviews due this round — outside MAX_SPACED_REP_PHRASES,
      // like the whole-seed rungs, so the LEGO reviews can never crowd them out.
      for (const due of seedSentenceReviewsDue(roundNumber, sentencesByHostRound)) {
        if (emitSeedSentence(due.seedNumber, { roundNumber, cycleNum: cycleNum + 1, offset: due.offset, offsetIndex: due.offsetIndex, hostRound: due.hostRound, usedPhrasesThisRound })) cycleNum++
      }

      // Phase 5: CONSOLIDATE ×2 - prefer unused USE phrases, allow reuse if pool exhausted
      let consolidateCount = 0
      const emitConsolidate = (phrase: Phrase) => {
        consolidateCount++
        cycleNum++
        emitItem({
          uuid: phraseCycleId(legoKey, phrase.id, 'use', cycleNum),
          cycleNum, roundNumber, seedId, legoKey,
          seedCode: seedId, legoCode: legoNum,
          type: 'use',
          knownText: phrase.known_text,
          targetText: phrase.target_text_roman || phrase.target_text,
          ...nativeFields(phrase),
          knownAudioId: phrase.known_audio_id,
          target1Id: phrase.target1_audio_id,
          target2Id: phrase.target2_audio_id,
          target1DurationMs: phrase.target1_duration_ms,
          target2DurationMs: phrase.target2_duration_ms,
          isNew: true,
        })
      }
      // CONSOLIDATE draws from the same known-side-filtered pool as review
      // (Tom named REVIEW and CONSOLIDATE together) — consolidate cycles ARE
      // use phrases. Debut BUILD/USE selection above is deliberately untouched.
      // (sortedUsePhrases already carries this round's word cap.)
      const consolidatePool = filterReviewPool(sortedUsePhrases, roundNumber, REVIEW_PULL_FILTER)
      // First pass: unused USE phrases
      for (const phrase of consolidatePool) {
        if (consolidateCount >= USE_CONSOLIDATION_COUNT) break
        const phraseId = getPhraseId(phrase.known_text, phrase.target_text)
        if (usedPhrasesThisRound.has(phraseId)) continue
        usedPhrasesThisRound.add(phraseId)
        emitConsolidate(phrase)
      }
      // Second pass: reuse USE phrases already used in BUILD (pool was too small)
      if (consolidateCount < USE_CONSOLIDATION_COUNT) {
        for (const phrase of consolidatePool) {
          if (consolidateCount >= USE_CONSOLIDATION_COUNT) break
          emitConsolidate(phrase)
        }
      }

      // Seed sentences HOSTED by this round play last in it — where their
      // nothing-new seed would have been in the running order.
      for (const seedNumber of sentencesByHostRound.get(roundNumber) ?? []) {
        if (emitSeedSentence(seedNumber, { roundNumber, cycleNum: cycleNum + 1, offset: 0, hostRound: roundNumber, usedPhrasesThisRound })) cycleNum++
      }

      // Layer 1 main-flow emission was removed 2026-05-19 (now the runtime
      // 30-cup wheel, useLayer1Scheduler.ts). Seed GRADUATION went with the
      // SEED-PHASE review tier on 2026-09-18: it existed only to let a
      // drained seed keep drawing whole-sentence production reviews, and
      // with reviews stopping at offset 89 a graduated seed can no longer be
      // due for anything. Nothing tracks it here now.

      // L2 (pod laps) stays runtime-scheduled by usePodLapScheduler —
      // every POD_ROUND_INTERVAL rounds from podActivationRound onward,
      // independent of main-round arithmetic. The script's emitPodLap /
      // l2FiresAt / podStageFor / STAGE_PLAYLIST / podSentences /
      // podRoundForMainRound helpers are intentionally retained for
      // hot-fix rollback to the in-script L2 path.
    }
  }

  // ==========================================================================
  // INFINITE-PLAY ROUNDS — review-only rounds after all new LEGOs introduced
  // ==========================================================================
  //
  // The course never ends. Once the main loop has introduced every new LEGO
  // from the range, we keep incrementing roundNumber and emitting review
  // rounds shaped as:
  //
  //   - target ~TARGET_ROUND_CYCLES (20) cycles per round
  //   - spaced-rep fills first via the same N-1, N-2, ..., N-89 fib-offset
  //     logic as the main loop, capped at MAX_SPACED_REP_PHRASES
  //   - random USE fills the remainder, with a floor of MIN_RANDOM_USE (10):
  //     one phrase each from distinct LEGOs sampled uniformly at random
  //     across the whole debuted inventory. (Previously recency-tiered, but
  //     in infinite play every LEGO has been debuted, so a recency bias
  //     becomes topic clustering toward the back end of the course.)
  //     Random-USE selections are deduped against this round's spaced-rep set.
  //
  // We do NOT mutate lastRound on random USE — the fib decay drains
  // naturally. Long-tail steady state is pure recency-biased USE.
  //
  // How many infinite-play rounds we generate is `infinitePlayLookahead`
  // rounds beyond the main loop's last round. After the full-course
  // refactor this is always the same number (default 50) regardless of
  // where the learner is — the script is one-shot whole-course; the
  // player consumes from wherever its cursor lands.

  // Canonical INF PLAY round (Tom's spec): 10 random USE + the full spaced
  // review (N-1×3, then N-2,N-3,N-5…N-89, capped at MAX_SPACED_REP_PHRASES=12)
  // = ~22 cycles. randomUseCount tops the random bucket back up to hold 22 as
  // spaced offsets drain (e.g. N-1 dropping off → +3 random), floored at 10.
  const TARGET_ROUND_CYCLES = 22
  const MIN_RANDOM_USE = 10
  const mainLoopLastRound = roundNumber
  inRevivalTail = true
  const revivalCap = mainLoopLastRound + infinitePlayLookahead

  while (roundNumber < revivalCap) {
    await yieldTick()
    roundNumber++
    const usedPhrasesThisRound = new Set<string>()
    let cycleNum = 0

    // Phase 1: SPACED-REP candidate set — same logic as the main loop.
    // LEGOs whose lastRound matches N-1, N-2, ..., N-89 from the current
    // round, skipping graduated-into-listening seeds.
    const dueForReview: { key: string; state: LegoState; fibPosition: number; phraseCount: number }[] = []
    const seenLegos = new Set<string>()
    for (let offsetIdx = 0; offsetIdx < SPACED_REP_OFFSETS.length; offsetIdx++) {
      const offset = SPACED_REP_OFFSETS[offsetIdx]
      const reviewRound = roundNumber - offset
      if (reviewRound < 1) break
      for (const [prevKey, state] of legoState.entries()) {
        if (seenLegos.has(prevKey)) continue
        if (state.lastRound === reviewRound) {
          const isN1 = offset === 1
          const phraseCount = isN1 ? N1_PHRASE_COUNT : 1
          dueForReview.push({ key: prevKey, state, fibPosition: offsetIdx, phraseCount })
          seenLegos.add(prevKey)
        }
      }
    }

    // Project how many spaced-rep cycles will actually fire (capped) so
    // we can size the random-USE bucket to maintain ~TARGET_ROUND_CYCLES.
    let projectedSpacedRep = 0
    for (const { phraseCount } of dueForReview) {
      if (projectedSpacedRep >= MAX_SPACED_REP_PHRASES) break
      projectedSpacedRep += Math.min(phraseCount, MAX_SPACED_REP_PHRASES - projectedSpacedRep)
    }
    const randomUseCount = Math.max(MIN_RANDOM_USE, TARGET_ROUND_CYCLES - projectedSpacedRep)

    // Phase 2: RANDOM USE selection — uniform random over ALL debuted LEGOs.
    //
    // Previous design used recency tiers (50% last 55 / 25% next 100 / 25%
    // rest), which made sense mid-course where "recent" tracks current
    // content. In infinite play every LEGO has been debuted, so the recency
    // bias becomes a topic-clustering bias toward the back end of the
    // course — for a 300-seed course that's half the round locked to ~26
    // seeds at the top. Uniform sampling gives the learner genuine variety
    // across the whole inventory.
    //
    // Deduped against the spaced-rep set so a LEGO can't appear twice in
    // one round.
    const spacedRepKeys = new Set(dueForReview.map(d => d.key))
    const allKeys = [...legoState.keys()]
    const pool = allKeys.filter(k => !spacedRepKeys.has(k))
    // Seeded in INF PLAY (infplayRandom supplied) so the random-USE tail is a
    // stable, navigable, reproducible stream rather than a per-session slot
    // machine. Falls back to Math.random for any legacy caller that omits it.
    const chosenKeys = sampleWithoutReplacement(pool, randomUseCount, infplayRandom ?? Math.random)

    // Phase 3: emit random USE (1 phrase per LEGO, advance round-robin
    // useIndex so phrases rotate across visits).
    if (shouldEmit()) {
      for (const legoKey of chosenKeys) {
        const state = legoState.get(legoKey)
        if (!state || state.usePhrases.length === 0) continue
        // Same word cap as every other USE draw. On a course whose main loop
        // ends before round 100 the tail is still inside the ladder, so it
        // cannot be assumed uncapped.
        const infUsePool = useWordCapped(state.usePhrases, roundNumber)
        const phrase = infUsePool[state.useIndex % infUsePool.length]
        state.useIndex++
        if (!phrase.known_audio_id || !phrase.target1_audio_id || !phrase.target2_audio_id) continue
        const phraseId = getPhraseId(phrase.known_text, phrase.target_text)
        if (usedPhrasesThisRound.has(phraseId)) continue
        usedPhrasesThisRound.add(phraseId)
        const legoNum = legoKey.match(/L(\d+)/)?.[1] || ''
        const seedId = legoKey.match(/S\d+/)?.[0] || ''
        cycleNum++
        emitItem({
          uuid: phraseCycleId(legoKey, phrase.id, `inf_R${roundNumber}`, cycleNum),
          cycleNum, roundNumber, seedId, legoKey,
          seedCode: seedId, legoCode: legoNum,
          type: 'use',
          knownText: phrase.known_text,
          targetText: phrase.target_text_roman || phrase.target_text,
          ...nativeFields(phrase),
          knownAudioId: phrase.known_audio_id,
          target1Id: phrase.target1_audio_id,
          target2Id: phrase.target2_audio_id,
          target1DurationMs: phrase.target1_duration_ms,
          target2DurationMs: phrase.target2_duration_ms,
          isNew: false,
        })
      }
    }

    // Phase 4: emit spaced rep — same shape as main-loop spaced rep.
    if (shouldEmit()) {
      let spacedRepCount = 0
      for (const { key: reviewKey, state, fibPosition, phraseCount } of dueForReview) {
        // The two WHOLE-SEED rungs sit OUTSIDE MAX_SPACED_REP_PHRASES. They
        // come last in offset order and the eleven use-phrase reviews ahead of
        // them fill the 12-slot budget exactly, so budgeting them meant rung
        // 144 fired for barely half the seeds — measured on ita_for_eng: 893
        // visits across 598 seeds, where 1,196 was the intended number.
        const isSeedRung = reviewItemIsSeed(SPACED_REP_OFFSETS[fibPosition])
        if (!isSeedRung && spacedRepCount >= MAX_SPACED_REP_PHRASES) break

        const reviewLegoNum = reviewKey.match(/L(\d+)/)?.[1] || ''
        const reviewSeedId = reviewKey.match(/S\d+/)?.[0] || ''

        // WHOLE-SEED rungs — same rule as the main loop's copy above.
        if (isSeedRung) {
          if (!seedAnchorLegos.has(reviewKey)) continue
          const emitted = emitSeedVisit(state.seedNum, {
            reviewKey, reviewSeedId, reviewLegoNum, roundNumber, fibPosition,
            reviewOf: state.lastRound, usedPhrasesThisRound,
            uuid: `${reviewKey}_inf_seedrep_R${roundNumber}`,
          })
          if (emitted) { spacedRepCount++; continue }
          // seed missing / incomplete audio → fall through to the use-phrase
          // review, which IS budgeted like any other.
          if (spacedRepCount >= MAX_SPACED_REP_PHRASES) continue
        }

        if (state.usePhrases.length === 0) continue

        // Same known-side pull filter as the main loop's review. Revival
        // rounds sit past the main loop, so on any real course this is already
        // beyond REVIEW_FILTER_MAX_ROUND and the basket is whole; it is here so
        // a short course's revival rounds obey the same rule as its main ones.
        const reviewPool = useWordCapped(filterReviewPool(state.usePhrases, roundNumber, REVIEW_PULL_FILTER), roundNumber)
        const phrasesToUse = Math.min(phraseCount, MAX_SPACED_REP_PHRASES - spacedRepCount, reviewPool.length)
        for (let i = 0; i < phrasesToUse; i++) {
          const phrase = reviewPool[state.useIndex % reviewPool.length]
          state.useIndex++
          const phraseId = getPhraseId(phrase.known_text, phrase.target_text)
          if (usedPhrasesThisRound.has(phraseId)) continue
          usedPhrasesThisRound.add(phraseId)
          if (!phrase.known_audio_id || !phrase.target1_audio_id || !phrase.target2_audio_id) continue
          cycleNum++
          spacedRepCount++
          emitItem({
            uuid: phraseCycleId(reviewKey, phrase.id, `inf_sr_R${roundNumber}`, cycleNum),
            cycleNum, roundNumber, seedId: reviewSeedId, legoKey: reviewKey,
            seedCode: reviewSeedId, legoCode: reviewLegoNum,
            type: 'spaced_rep',
            knownText: phrase.known_text,
            targetText: phrase.target_text_roman || phrase.target_text,
            ...nativeFields(phrase),
            knownAudioId: phrase.known_audio_id,
            target1Id: phrase.target1_audio_id,
            target2Id: phrase.target2_audio_id,
            target1DurationMs: phrase.target1_duration_ms,
            target2DurationMs: phrase.target2_duration_ms,
            isNew: false,
            fibPosition,
            reviewOf: state.lastRound,
          })
        }
      }
    }

    // Seed-sentence reviews whose schedule runs past the main loop keep their
    // rungs here (off unless the course is switched on — see the main loop).
    for (const due of seedSentenceReviewsDue(roundNumber, sentencesByHostRound)) {
      if (emitSeedSentence(due.seedNumber, { roundNumber, cycleNum: cycleNum + 1, offset: due.offset, offsetIndex: due.offsetIndex, hostRound: due.hostRound, usedPhrasesThisRound })) cycleNum++
    }

    // L1 listening removed from infinite-play main flow 2026-05-19 —
    // moved to Listening MODE. L2 stays runtime-scheduled (unchanged).

    // Safety: if nothing emitted (no usable LEGOs at all), stop — otherwise
    // we'd loop emitting empty rounds.
    if (cycleNum === 0) break
  }

  // Decompose phrases into component LEGO IDs
  let decomposedCount = 0
  for (const item of items) {
    await yieldTick()
    if (item.type === 'intro' || item.type === 'debut' || item.type === 'listening' || item.type === 'component_intro' || item.type === 'component_practice' || item.type === 'pod' || item.type === 'listen_intro' || item.type === 'listen_outro') continue
    const components = decomposePhrase(item.targetText)
    if (components.length > 0) {
      item.componentLegoIds = components
      item.componentLegoTexts = components.map(id => legoIdToText.get(id) || '')
      if (courseHasRomanized) {
        item.componentLegoTextsNative = components.map(id => legoIdToTextNative.get(id) || legoIdToText.get(id) || '')
      }
      decomposedCount++
    }
  }
  console.debug(`[generateLearningScript] Decomposed ${decomposedCount}/${items.filter(i => i.type !== 'intro' && i.type !== 'debut').length} phrases into LEGO components (${legoTextMap.size} LEGOs in map)`)

  // Remove consecutive duplicates (matching dashboard logic)
  const dedupedItems: ScriptItem[] = []
  let lastNonIntroItem: ScriptItem | null = null

  for (const item of items) {
    await yieldTick()
    // Exempt: types whose repetition is the design, not an accident.
    if (item.type === 'intro' || item.type === 'debut' || item.type === 'listening' || item.type === 'component_intro' || item.type === 'pod' || item.type === 'listen_intro' || item.type === 'listen_outro') {
      dedupedItems.push(item)
      continue
    }

    if (lastNonIntroItem) {
      const sameKnown = normalizeText(item.knownText) === normalizeText(lastNonIntroItem.knownText)
      const sameTarget = normalizeText(item.targetText) === normalizeText(lastNonIntroItem.targetText)
      if (sameKnown && sameTarget) continue
    }

    dedupedItems.push(item)
    lastNonIntroItem = item
  }

  const removedCount = items.length - dedupedItems.length

  // Drop individual cycles missing required text (partially-built phrases).
  // Per-cycle filtering preserves the good cycles in a partially-incomplete
  // round; whole-round filtering preserves nothing if even one cycle is good.
  //
  // The whole-round "no audio at all" drop that used to live here was removed
  // 2026-08-06 with the per-LEGO audio filter it partnered: it keyed off an
  // intro/debut with no target1Id, and killed that round's build, use, review
  // and consolidate cycles with it — the second of the two amputations. Intros
  // and debuts are now only EMITTED when their own audio resolves, so a round
  // with an audio hole simply arrives without them and keeps everything else;
  // a round left with nothing at all is dropped downstream by toSimpleRounds.
  //
  // Listening items (pod/bookend) are exempt: pod sentence plays have empty
  // knownText, pod translation plays have empty targetText, and bookends
  // have empty targetText. These are by design — the text-completeness check
  // is for unbuilt LEGO/phrase rows, not for listening cycles whose missing
  // side reflects their play role.
  const TEXT_CHECK_EXEMPT = new Set(['pod', 'listen_intro', 'listen_outro', 'listening'])
  let droppedByText = 0
  const playableItems = dedupedItems.filter(item => {
    if (TEXT_CHECK_EXEMPT.has(item.type)) return true
    const knownOk = typeof item.knownText === 'string' && item.knownText.trim().length > 0
    const targetOk = typeof item.targetText === 'string' && item.targetText.trim().length > 0
    if (!knownOk || !targetOk) {
      droppedByText++
      return false
    }
    return true
  })

  // ── EASY doubling (Tom, 2026-08-07) ──────────────────────────────────────
  // "in EASY mode, double up every phrase, every BLD, every USE, every REVIEW,
  // every CONSOLIDATE". BOTH the count and the eligible types are config, read
  // off the mode row. Runs AFTER the consecutive-duplicate removal above, which
  // would strip the second copy, and BEFORE the A-64 floor below, which allows
  // exactly two in a row and so guarantees "doubled" can never become tripled.
  // A count of 1 (Fast) returns the input array untouched, so Fast's script is
  // byte-identical to the pre-2026-08-07 walk.
  const doubledItems = repeatPhraseCycles(playableItems, {
    count: normalizePhraseRepeatCount(easyOptions.phraseRepeatCount),
    types: normalizeRepeatedCycleTypes(easyOptions.repeatedCycleTypes),
  })

  // ── A-64 floor (Tom, 2026-08-06) ─────────────────────────────────────────
  // "No mode should ever repeat the same prompt more than twice consecutively."
  //
  // The consecutive-duplicate pass above is stricter than the law for ordinary
  // cycles, but it lets intro/debut/pod/bookend types straight through — so a
  // pod lap whose stage playlist fires the same clip three times, or any future
  // emitter, can still breach. This pass is the floor that cannot be breached:
  // it runs downstream of every script-shape value (Easy mode's doubled
  // n1PhraseCount included) and of the pod stage playlists in algorithm_config.
  //
  // Re-interleaving is confined to a single round: rounds are the player's unit
  // of position, so an item must never migrate across a round boundary. Each
  // round is seeded with the previous round's tail so the law also holds at the
  // seam. Totals survive wherever a round holds anything to interleave with.
  const roundCapped: ScriptItem[] = []
  let capTail: string[] = []
  let capDropped = 0
  let capReordered = 0
  let currentRound: number | null = null
  let roundBuffer: ScriptItem[] = []
  const flushRound = () => {
    if (roundBuffer.length === 0) return
    const capped = capConsecutiveRepeats(roundBuffer, scriptItemIdentity, { seed: capTail })
    capTail = capped.tail
    capDropped += capped.dropped.length
    if (capped.reordered) capReordered++
    roundCapped.push(...capped.items)
    roundBuffer = []
  }
  for (const item of doubledItems) {
    await yieldTick()
    if (currentRound !== null && item.roundNumber !== currentRound) flushRound()
    currentRound = item.roundNumber
    roundBuffer.push(item)
  }
  flushRound()
  if (capReordered > 0 || capDropped > 0) {
    console.info(`[generateLearningScript] A-64 cap: re-interleaved ${capReordered} round(s)${capDropped > 0 ? `, dropped ${capDropped} rep(s) with nothing to interleave against` : ''}`)
  }

  if (introsSkippedForAudio > 0 || debutsSkippedForAudio > 0 || droppedByText > 0) {
    console.info(`[generateLearningScript] Suppressed ${introsSkippedForAudio} intros / ${debutsSkippedForAudio} debuts for missing audio (their rounds still play), ${droppedByText} missing-text cycles`)
  }

  // Validate generated script integrity in dev mode only — production cold
  // start doesn't benefit from re-checking script integrity at runtime, and
  // validating a 9999-round script costs hundreds of ms on in-progress
  // courses where most rounds end up with errors anyway.
  // Defensive: import.meta.env is Vite-specific, so guard for non-Vite hosts
  // (e.g. running this module under tsx for diagnostic scripts).
  const isDevBuild = (() => {
    try { return !!(import.meta as any)?.env?.DEV } catch { return false }
  })()
  if (isDevBuild) {
    const validationReport = validateLearningScript(roundCapped)
    if (!validationReport.valid) {
      console.warn(`[generateLearningScript] Validation: ${validationReport.summary}`)
    }
  }

  // Summary: intros missing presentation audio (single log instead of per-item spam)
  if (introsMissingAudio.length > 0) {
    console.warn(`[generateLearningScript] ${introsMissingAudio.length} intros missing presentation audio — will play target audio only`)
  }

  // Recount rounds from playable items
  const playableRoundCount = new Set(roundCapped.map(i => i.roundNumber)).size
  // Where the INF-PLAY revival tail begins = count of PLAYABLE main-loop rounds.
  // mainLoopLastRound is the generator's own boundary (set right before the
  // revival loop); counting distinct playable roundNumbers at-or-below it gives
  // the true current course size, with unbuilt/no-audio rounds already filtered
  // out. This is what the player must use to find the tail — never a DB count.
  const mainLoopRoundCount = new Set(
    roundCapped.filter(i => i.roundNumber <= mainLoopLastRound).map(i => i.roundNumber)
  ).size
  const listeningStats = ''
  console.debug(`[generateLearningScript] ${roundCapped.length} items, ${playableRoundCount} rounds for ${courseCode}${removedCount > 0 ? `, ${removedCount} deduped` : ''}${introsSkippedForAudio + debutsSkippedForAudio > 0 ? `, ${introsSkippedForAudio + debutsSkippedForAudio} no-audio intro/debut cycles` : ''}${droppedByText > 0 ? `, ${droppedByText} bad-text cycles` : ''}${listeningStats}`)
  let seedSentenceStats: LearningScriptResult['seedSentences']
  if (SEED_SENTENCES_ON) {
    let hosted = 0
    for (const seeds of sentencesByHostRound.values()) hosted += seeds.length
    seedSentenceStats = { hosted, skippedMissingAudio: sentenceSeedsMissingClips.size, unhosted: sentenceSeedsUnhosted }
    console.info(
      `[generateLearningScript] seed sentences (${courseCode}): ${hosted} nothing-new seed(s) hosted, ` +
      `${sentenceSeedsMissingClips.size} skipped for missing seed audio, ${sentenceSeedsUnhosted} before any round`,
    )
  }
  await stampSeedVisitDurations(supabase, roundCapped)

  return {
    items: roundCapped, cycleCount: roundCapped.length, roundCount: playableRoundCount, mainLoopRoundCount, hasRomanizedText: courseHasRomanized, syllableCapApplied, useWordCapApplied,
    // Present ONLY on a course with seed-sentence review switched on, so every
    // other course's result keeps exactly the keys it always had.
    ...(seedSentenceStats ? { seedSentences: seedSentenceStats } : {}),
  }
}
